import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buildStxEtxAsciiLenPacket,
  convertEn,
  drainA1Frames,
  getFormattedDate,
  INT_MAX,
  parseDikType1,
  parseSpSnType1Step2,
  setActionLog,
  startReading,
  startSerialRxMonitor,
  stopSerialRxMonitor,
  xorChecksumByte,
} from "../utils/util";
import { useCanWrite } from "../utils/useCanWrite";
import {
  Box,
  ActionIcon,
  Button,
  Flex,
  Paper,
  Text,
  TextInput,
  Badge,
  Modal,
  SegmentedControl,
  Select,
} from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconSearch,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import classes from "./KeyInjectTest.module.css";
import { useWorkEnvironment } from "../contexts/WorkEnvironmentContext";
import {
  WORK_ENV_MODEL_MISMATCH_MSG,
  applyWorkEnvironmentToParams,
  isWorkEnvironmentModelMismatch,
} from "../utils/injectWorkEnvironment";

type SessionPhase = "serial" | "dik" | "done";
type InjectMode = "auto" | "manual";
/** S/N·DIK 케이블 vs MAC 케이블. 두 COM을 동시에 연다. */
type PortRole = "sn" | "mac";

interface TerminalInjectInfo {
  terminalId: number;
  serialNo: string | null;
  spSerialNo: string;
  macAddress: string | null;
  internalModelName: string | null;
  externalModelName: string | null;
  spSerialInjectionYn: string;
  macInjectionYn: string;
  refInjectionYn: string;
  vanType: string | null;
  securityKeyRef1: string | null;
  securityKeyRef2: string | null;
  dikYn?: string;
}

/** GET /api/v1/terminal/list 응답 행 */
interface TerminalListRow {
  terminalId: number;
  serialNo?: string | null;
  spSerialNo: string;
  macAddress?: string | null;
  internalModelName?: string | null;
  externalModelName?: string | null;
  spSerialInjectionYn?: string;
  macInjectionYn?: string;
  refInjectionYn?: string;
  vanType?: string | null;
  securityKeyRef1?: string | null;
  securityKeyRef2?: string | null;
  terminalModelId?: number;
  dikYn?: string;
}

interface TerminalGroupOption {
  terminalGroupId: number;
  aliasName: string;
}

const mapListRowToInjectInfo = (row: TerminalListRow): TerminalInjectInfo => ({
  terminalId: row.terminalId,
  serialNo: row.serialNo ?? null,
  spSerialNo: row.spSerialNo ?? "",
  macAddress: row.macAddress ?? null,
  internalModelName: row.internalModelName ?? null,
  externalModelName: row.externalModelName ?? null,
  spSerialInjectionYn: row.spSerialInjectionYn ?? "N",
  macInjectionYn: row.macInjectionYn ?? "N",
  refInjectionYn: row.refInjectionYn ?? "N",
  vanType: row.vanType ?? null,
  securityKeyRef1: row.securityKeyRef1 ?? null,
  securityKeyRef2: row.securityKeyRef2 ?? null,
  dikYn: row.dikYn,
});

type LogGroupType = "connect" | "serial" | "mac" | "dik" | "system";
type LogTone = "default" | "ok" | "err" | "detail";

interface LogLine {
  time: string;
  text: string;
  tone: LogTone;
}

interface LogGroup {
  id: number;
  type: LogGroupType;
  title: string;
  time: string;
  lines: LogLine[];
}

const GROUP_STYLE: Record<
  LogGroupType,
  { border: string; bg: string; titleColor: string }
> = {
  connect: { border: "#3b82f6", bg: "#eff6ff", titleColor: "#1d4ed8" },
  serial: { border: "#0d9488", bg: "#f0fdfa", titleColor: "#0f766e" },
  mac: { border: "#7c3aed", bg: "#f5f3ff", titleColor: "#6d28d9" },
  dik: { border: "#c2410c", bg: "#fff7ed", titleColor: "#9a3412" },
  system: { border: "#94a3b8", bg: "#f8fafc", titleColor: "#475569" },
};

const bytesToHex = (bytes: number[] | Uint8Array): string =>
  Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join(" ");

const bytesToAscii = (bytes: number[] | Uint8Array): string =>
  Array.from(bytes)
    .map((b) => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : "."))
    .join("");

const isInjected = (yn: string | null | undefined) =>
  (yn ?? "").trim().toUpperCase() === "Y";

const hasDikKey = (row: {
  securityKeyRef1?: string | null;
  securityKeyRef2?: string | null;
}) =>
  Boolean(
    (row.securityKeyRef1 ?? "").trim() || (row.securityKeyRef2 ?? "").trim(),
  );

const sessionPhaseFromInjectFlags = (row: {
  spSerialInjectionYn?: string;
  refInjectionYn?: string;
  securityKeyRef1?: string | null;
  securityKeyRef2?: string | null;
}): SessionPhase => deriveSessionPhase(row, hasDikKey(row));

const isDikLineEnvironment = (dikYn?: string | null) =>
  String(dikYn || "N").toUpperCase() === "Y";

const dikStepRequired = (
  row: {
    securityKeyRef1?: string | null;
    securityKeyRef2?: string | null;
  },
  dikLine: boolean,
) => dikLine || hasDikKey(row);

const deriveSessionPhase = (
  row: {
    spSerialInjectionYn?: string;
    refInjectionYn?: string;
    macInjectionYn?: string;
    securityKeyRef1?: string | null;
    securityKeyRef2?: string | null;
  },
  dikLine: boolean,
): SessionPhase => {
  void dikLine;
  if (!isInjected(row.spSerialInjectionYn)) return "serial";
  if (!isInjected(row.refInjectionYn)) return "dik";
  return "done";
};

const hasRegisteredDik = (row: {
  securityKeyRef1?: string | null;
  securityKeyRef2?: string | null;
}) => {
  const dikSn = (row.securityKeyRef1 ?? "").trim();
  const dik = (row.securityKeyRef2 ?? "").trim();
  return Boolean(dikSn && dik);
};

const buildDikPacket = (dikSn: string, dik: string): Uint8Array => {
  const arr = new Uint8Array(53);
  let idx = 0;
  arr[idx++] = 0x02;
  arr[idx++] = 0x3d;
  arr[idx++] = 0x00;
  arr[idx++] = 0x48;
  const sn = new TextEncoder().encode(`${dikSn}${dik}`);
  arr.set(sn, idx);
  idx += sn.length;
  arr[idx] = xorChecksumByte(arr);
  return arr;
};

type SerialParser = {
  (byte: number, buffer: number[]): number[] | null;
  reset?: () => void;
};

const isInjectWorkComplete = (row: {
  spSerialInjectionYn?: string;
  refInjectionYn?: string;
  macInjectionYn?: string;
  securityKeyRef1?: string | null;
  securityKeyRef2?: string | null;
}) => {
  if (!isInjected(row.spSerialInjectionYn) || !isInjected(row.macInjectionYn)) {
    return false;
  }
  if (!hasDikKey(row)) return true;
  return isInjected(row.refInjectionYn);
};

/** 조회 기준 앵커 + 범위 목록 (중복 제거). 현재 작업 S/N이 앵커와 달라도 목록에서 빠지지 않음 */
const buildDisplayQueue = (
  anchor: TerminalListRow | null,
  rangeList: TerminalListRow[],
): TerminalListRow[] => {
  const out: TerminalListRow[] = [];
  const seen = new Set<string>();
  if (anchor?.spSerialNo?.trim()) {
    out.push(anchor);
    seen.add(anchor.spSerialNo.trim());
  }
  for (const row of rangeList) {
    const sn = (row.spSerialNo || "").trim();
    if (!sn || seen.has(sn)) continue;
    out.push(row);
    seen.add(sn);
  }
  return out;
};

/** 화살표 이동용 (관련 S/N 전체). 자동 다음 주입은 미완료만 사용 */
const buildNavQueue = (
  anchor: TerminalListRow | null,
  rangeList: TerminalListRow[],
): TerminalListRow[] => buildDisplayQueue(anchor, rangeList);

/** 현재 작업 단말이 큐에 없으면 합성해 넣음 (주입 목록에 항상 표시) */
const ensureCurrentInQueue = (
  rows: TerminalListRow[],
  current: TerminalInjectInfo | null,
): TerminalListRow[] => {
  if (!current?.spSerialNo?.trim()) return rows;
  const sn = current.spSerialNo.trim();
  if (rows.some((r) => r.spSerialNo === sn)) return rows;
  const synthetic: TerminalListRow = {
    terminalId: current.terminalId,
    serialNo: current.serialNo,
    spSerialNo: sn,
    macAddress: current.macAddress,
    internalModelName: current.internalModelName,
    externalModelName: current.externalModelName,
    spSerialInjectionYn: current.spSerialInjectionYn,
    macInjectionYn: current.macInjectionYn,
    refInjectionYn: current.refInjectionYn,
    vanType: current.vanType,
    securityKeyRef1: current.securityKeyRef1,
    securityKeyRef2: current.securityKeyRef2,
  };
  return [...rows, synthetic];
};

const countPendingInject = (rows: TerminalListRow[]) =>
  rows.filter((row) => !isInjectWorkComplete(row)).length;

const findNextIncompleteRow = (
  ordered: TerminalListRow[],
  afterSn: string | null,
): TerminalListRow | null => {
  if (!ordered.length) return null;
  const startIdx = afterSn
    ? ordered.findIndex((row) => row.spSerialNo === afterSn)
    : -1;
  for (let i = Math.max(0, startIdx + 1); i < ordered.length; i++) {
    if (!isInjectWorkComplete(ordered[i])) return ordered[i];
  }
  return null;
};

const parseSerialSequence = (value: string) => {
  const match = value.trim().match(/^(.*?)(\d+)$/);
  if (!match) return null;
  return {
    prefix: match[1],
    numberText: match[2],
    number: Number(match[2]),
  };
};

const compareSerialSequence = (a: string, b: string) => {
  const parsedA = parseSerialSequence(a);
  const parsedB = parseSerialSequence(b);
  if (
    parsedA &&
    parsedB &&
    parsedA.prefix === parsedB.prefix &&
    parsedA.numberText.length === parsedB.numberText.length
  ) {
    return parsedA.number - parsedB.number;
  }
  return a.localeCompare(b);
};

const sortSerialRows = (rows: TerminalListRow[], descending: boolean) =>
  [...rows].sort((a, b) => {
    const order = compareSerialSequence(a.spSerialNo, b.spSerialNo);
    return descending ? -order : order;
  });

const formatSerialNumber = (
  prefix: string,
  number: number,
  numberLength: number,
) => `${prefix}${String(number).padStart(numberLength, "0")}`;

/** previous ↔ current 사이 중간 시리얼 목록 (없으면 null, 연속이면 빈 배열이 아닌 null 처리용 empty 아님) */
const listIntermediateSerials = (
  previous: string,
  current: string,
): string[] | null => {
  const parsedPrev = parseSerialSequence(previous);
  const parsedCurrent = parseSerialSequence(current);
  if (
    !parsedPrev ||
    !parsedCurrent ||
    parsedPrev.prefix !== parsedCurrent.prefix ||
    parsedPrev.numberText.length !== parsedCurrent.numberText.length
  ) {
    return null;
  }
  const diff = Math.abs(parsedCurrent.number - parsedPrev.number);
  if (diff <= 1) return [];

  const start = Math.min(parsedPrev.number, parsedCurrent.number) + 1;
  const end = Math.max(parsedPrev.number, parsedCurrent.number) - 1;
  const out: string[] = [];
  for (let n = start; n <= end; n++) {
    out.push(
      formatSerialNumber(parsedPrev.prefix, n, parsedPrev.numberText.length),
    );
  }
  return out;
};

const formatSerialListLabel = (sns: string[]) => {
  if (!sns.length) return "";
  if (sns.length === 1) return sns[0];
  if (sns.length === 2) return `${sns[0]}, ${sns[1]}`;
  return `${sns[0]} ~ ${sns[sns.length - 1]} (${sns.length}건)`;
};

type SequenceSkipKind = "missing" | "already_done" | "mixed";

interface SequenceSkipNotice {
  kind: SequenceSkipKind;
  title: string;
  message: string;
}

/**
 * 시리얼 번호가 건너뛸 때:
 * - 목록에 있고 주입완료 → 이미 완료
 * - 목록에 없음 → 중간 번호 누락
 */
const buildSequenceSkipNotice = (
  previous: string,
  current: string,
  knownRows: TerminalListRow[],
): SequenceSkipNotice | null => {
  const mids = listIntermediateSerials(previous, current);
  if (mids == null || mids.length === 0) return null;

  const bySn = new Map(
    knownRows
      .filter((r) => r.spSerialNo?.trim())
      .map((r) => [r.spSerialNo.trim(), r] as const),
  );

  const done: string[] = [];
  const missing: string[] = [];
  const incomplete: string[] = [];

  for (const sn of mids) {
    const row = bySn.get(sn);
    if (!row) missing.push(sn);
    else if (isInjectWorkComplete(row)) done.push(sn);
    else incomplete.push(sn);
  }

  // 중간 번호가 모두 목록에 있고 주입완료 → 누락이 아님
  if (missing.length === 0 && incomplete.length === 0 && done.length > 0) {
    const label = formatSerialListLabel(done);
    return {
      kind: "already_done",
      title: "이미 주입 완료",
      message:
        done.length === 1
          ? `이미 주입 완료: ${label}`
          : `이미 주입 완료(건너뜀): ${label}`,
    };
  }

  if (done.length === 0 && incomplete.length === 0 && missing.length > 0) {
    const label = formatSerialListLabel(missing);
    return {
      kind: "missing",
      title: "중간 번호 누락",
      message:
        missing.length === 1
          ? `중간 번호 누락: ${label}`
          : `중간 번호 누락: ${label}`,
    };
  }

  const parts: string[] = [];
  if (done.length) parts.push(`이미 완료 ${formatSerialListLabel(done)}`);
  if (missing.length) parts.push(`누락 ${formatSerialListLabel(missing)}`);
  if (incomplete.length) {
    parts.push(`미완료 ${formatSerialListLabel(incomplete)}`);
  }
  return {
    kind: "mixed",
    title: "시리얼 번호 확인",
    message: parts.join(" · "),
  };
};

const filterRowsByPrefix = (rows: TerminalListRow[], prefix: string) => {
  const p = prefix.trim().toUpperCase();
  if (!p) return rows;
  return rows.filter((row) =>
    (row.spSerialNo || "").toUpperCase().startsWith(p),
  );
};

/** 화면 표시용 MAC (전송값에는 사용하지 않음)
 * - C4E7BE0A5D8E → C4E7BE 0A5D8E (12자리 hex는 6+6)
 */
const formatMacForDisplay = (mac: string | null | undefined) => {
  const value = (mac ?? "").trim();
  if (!value) return "";
  const compact = value.replace(/[\s:\-]/g, "").toUpperCase();
  if (/^[0-9A-F]{12}$/.test(compact)) {
    return `${compact.slice(0, 6)} ${compact.slice(6)}`;
  }
  return value;
};

/** DIK 성공 요약 표시: 앞 3자리(000) 제거 후 5자리 (전체 key1 전체 key2)
 * 예: 0007156523040400, 97BF... -> 71565 (0007156523040400 97BF...)
 */
const formatDikSuccessSummary = (key1: string, key2: string) => {
  const k1 = (key1 ?? "").trim();
  const k2 = (key2 ?? "").trim();
  let prefix = "";
  if (k1.length >= 8) {
    prefix = k1.slice(3, 8);
  } else if (k1.length > 3) {
    prefix = k1.slice(3);
  } else {
    prefix = k1;
  }
  return `${prefix} (${k1} ${k2})`.trim();
};

/** DIK 표시용: 앞 3자리 제외 후 5자리 추출 (예: 0007156523040400 → 71565) */
const formatDikDisplay = (dik: string | null | undefined) => {
  const value = (dik ?? "").trim();
  if (!value) return "";
  if (value.length >= 8) {
    return value.slice(3, 8);
  }
  if (value.length > 3) {
    return value.slice(3);
  }
  return value;
};

/** 한글 IME 상태에서도 물리 키(영문) 기준으로 문자 추출 */
const physicalCharFromKeyboardEvent = (e: {
  code: string;
  key: string;
  shiftKey: boolean;
}): string | null => {
  const { code } = e;
  if (code.startsWith("Key") && code.length === 4) {
    const letter = code.slice(3);
    return e.shiftKey ? letter.toUpperCase() : letter.toLowerCase();
  }
  if (code.startsWith("Digit") && code.length === 6) {
    const digit = code.slice(5);
    if (!e.shiftKey) return digit;
    const shifted: Record<string, string> = {
      "1": "!",
      "2": "@",
      "3": "#",
      "4": "$",
      "5": "%",
      "6": "^",
      "7": "&",
      "8": "*",
      "9": "(",
      "0": ")",
    };
    return shifted[digit] ?? digit;
  }
  if (code.startsWith("Numpad") && code.length === 7) {
    const digit = code.slice(6);
    if (/^\d$/.test(digit)) return digit;
  }
  if (code === "Minus" || code === "NumpadSubtract") return "-";
  if (code === "Equal") return e.shiftKey ? "+" : "=";
  if (code === "Period" || code === "NumpadDecimal") return ".";
  if (code === "Slash") return e.shiftKey ? "?" : "/";
  // 이미 영문/숫자면 key 사용
  if (e.key.length === 1 && /[A-Za-z0-9\-_.]/.test(e.key)) return e.key;
  return null;
};

/** 바코드 스캔값: 한글 자모 → 영문 QWERTY 복원 후 대문자 */
const normalizeBarcodeScan = (raw: string) =>
  convertEn(raw.replace(/\s/g, "")).trim().toUpperCase();

const waitUntilReadableUnlocked = async (
  port: SerialPort,
  timeoutMs = 800,
): Promise<boolean> => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (!port.readable?.locked) return true;
    await new Promise((r) => setTimeout(r, 20));
  }
  return !port.readable?.locked;
};

const delayMs = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** 노이즈 버퍼에서 STX A3 LEN DATA ETX LRC 를 찾는다 */
const scanA3Payload = (bytes: number[]): number[] | null => {
  for (let i = 0; i <= bytes.length - 6; i++) {
    if (bytes[i] !== 0x02 || bytes[i + 1] !== 0xa3) continue;
    const hi = bytes[i + 2];
    const lo = bytes[i + 3];
    if (hi < 0x30 || hi > 0x39 || lo < 0x30 || lo > 0x39) continue;
    const len = (hi - 0x30) * 10 + (lo - 0x30);
    const total = 6 + len;
    if (i + total > bytes.length) continue;
    if (bytes[i + 4 + len] !== 0x03) continue;
    const frame = bytes.slice(i, i + total);
    const lrc = frame.slice(0, -1).reduce((acc, v) => acc ^ v, 0);
    if (lrc !== frame[frame.length - 1]) continue;
    return frame.slice(4, 4 + len);
  }
  return null;
};

const KeyInjectTest: React.FC<{ menuTitle: string }> = ({ menuTitle }) => {
  const canWrite = useCanWrite();
  const { workEnvironment } = useWorkEnvironment();
  const workEnvironmentRef = useRef(workEnvironment);
  useEffect(() => {
    workEnvironmentRef.current = workEnvironment;
  }, [workEnvironment]);
  const snPortRef = useRef<SerialPort | null>(null);
  const macPortRef = useRef<SerialPort | null>(null);
  const snMonitorReaderRef =
    useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const macMonitorReaderRef =
    useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const serialReceiveRef =
    useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const snRxBufferRef = useRef<number[]>([]);
  const macRxBufferRef = useRef<number[]>([]);
  const lastA2SentAtRef = useRef(0);
  /** 동일 A1 대기 구간에서 안내 로그 1회만 */
  const a1AnnouncedRef = useRef(false);
  const autoPendingRef = useRef(false);
  const injectModeRef = useRef<InjectMode>("auto");
  const isBusyRef = useRef(false);
  const lastDikFailAtRef = useRef(0);
  /** S/N 직후 잔여 RX로 DIK가 바로 나가지 않게 */
  const dikIgnoreRxUntilRef = useRef(0);
  const a3WaiterRef = useRef<{
    expect: string;
    buffer: number[];
    resolve: (payload: number[] | null) => void;
  } | null>(null);
  const pendingMacAckRef = useRef<{
    expect: string;
    term: TerminalInjectInfo;
    until: number;
  } | null>(null);
  const logScrollRef = useRef<HTMLDivElement>(null);
  const scanBufferRef = useRef("");
  const onBarcodeScanRef = useRef<(raw: string) => void>(() => { });
  const groupIdRef = useRef(0);
  const activeGroupIdRef = useRef<number | null>(null);

  const sendServerDebugLog = (action: string, content: any) => {
    try {
      void setActionLog({
        eventType: "KEY_INJECT" as any,
        title: action,
        serialNo: terminalRef.current?.spSerialNo || undefined,
        content:
          typeof content === "object"
            ? JSON.stringify(content)
            : String(content),
      });
    } catch {
      /* ignore */
    }
  };

  const [searchSerial, setSearchSerial] = useState("");
  const [selectedTerminalGroupId, setSelectedTerminalGroupId] = useState<
    string | null
  >(null);
  const [terminalGroups, setTerminalGroups] = useState<TerminalGroupOption[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [terminal, setTerminal] = useState<TerminalInjectInfo | null>(null);
  const terminalRef = useRef<TerminalInjectInfo | null>(null);
  const [pendingList, setPendingList] = useState<TerminalListRow[]>([]);
  const [anchorRow, setAnchorRow] = useState<TerminalListRow | null>(null);
  const [queueFrom, setQueueFrom] = useState("");
  const queueFromRef = useRef("");
  /** 조회 시 고정한 범위 기준 S/N (주입 후 다음 이동 시에도 유지) */
  const queueRangeAnchorRef = useRef("");
  const lastCompletedSerialRef = useRef<string | null>(null);
  /** 이번 작업 세션 단계. DB 완료 여부가 아니라 A1이 무엇을 보낼지 결정 */
  const sessionPhaseRef = useRef<SessionPhase>("serial");
  const [sessionPhase, setSessionPhaseState] =
    useState<SessionPhase>("serial");

  const setSessionPhase = (phase: SessionPhase) => {
    sessionPhaseRef.current = phase;
    setSessionPhaseState(phase);
  };

  const [isSnPortOpen, setIsSnPortOpen] = useState(false);
  const [isMacPortOpen, setIsMacPortOpen] = useState(false);
  const openNextPortRef = useRef<() => Promise<void>>(async () => { });
  const closePortRef = useRef<() => Promise<void>>(async () => { });
  const onSendSerialRef = useRef<() => Promise<void>>(async () => { });
  const onSendDikRef = useRef<() => Promise<void>>(async () => { });
  const onSendMacRef = useRef<() => Promise<void>>(async () => { });
  const [snListening, setSnListening] = useState(false);
  const [macListening, setMacListening] = useState(false);
  const [a1Ready, setA1Ready] = useState(false);
  const [injectMode, setInjectMode] = useState<InjectMode>("auto");
  const [logGroups, setLogGroups] = useState<LogGroup[]>([]);
  const [showSerialDetail, setShowSerialDetail] = useState(false);
  const showSerialDetailRef = useRef(false);
  const [queueDescending, setQueueDescending] = useState(false);
  /** A1 콜백 stale closure 방지: 최신 오름/내림 고정 */
  const queueDescendingRef = useRef(false);
  const selectedTerminalGroupIdRef = useRef<string | null>(null);
  const loadInjectQueueRef = useRef<
    (
      fromSpSerialNo: string,
      options?: { silent?: boolean; advanceToNext?: boolean },
    ) => Promise<void>
  >(async () => { });
  const onSnRxChunkRef = useRef<(bytes: number[]) => void>(() => { });
  const onMacRxChunkRef = useRef<(bytes: number[]) => void>(() => { });
  const [sequenceGap, setSequenceGap] = useState<SequenceSkipNotice | null>(
    null,
  );
  const [gapModal, setGapModal] = useState<SequenceSkipNotice | null>(null);

  useEffect(() => {
    terminalRef.current = terminal;
  }, [terminal]);

  useEffect(() => {
    injectModeRef.current = injectMode;
  }, [injectMode]);

  useEffect(() => {
    queueDescendingRef.current = queueDescending;
  }, [queueDescending]);

  useEffect(() => {
    selectedTerminalGroupIdRef.current = selectedTerminalGroupId;
  }, [selectedTerminalGroupId]);

  const appendToActiveGroup = (text: string, tone: LogTone = "default") => {
    const gid = activeGroupIdRef.current;
    if (gid == null) return;
    const time = getFormattedDate();
    setLogGroups((prev) =>
      prev.map((g) =>
        g.id === gid ? { ...g, lines: [...g.lines, { time, text, tone }] } : g,
      ),
    );
  };

  const startLogGroup = (type: LogGroupType, title: string) => {
    const id = ++groupIdRef.current;
    activeGroupIdRef.current = id;
    const time = getFormattedDate();
    setLogGroups((prev) => [
      ...prev.slice(-30),
      { id, type, title, time, lines: [] },
    ]);
    return id;
  };

  const endActiveGroup = () => {
    activeGroupIdRef.current = null;
  };

  const clearLogs = () => {
    activeGroupIdRef.current = null;
    setLogGroups([]);
  };

  const logSystem = (text: string, tone: LogTone = "default") => {
    if (activeGroupIdRef.current == null) startLogGroup("system", "시스템");
    appendToActiveGroup(text, tone);
    endActiveGroup();
  };

  const cableCount = workEnvironment?.serialCableCount ?? 1;
  const isSinglePort = cableCount <= 1;
  const wireLineOf = (role: PortRole) => (role === "mac" ? "MAC" : "S/N");

  /** 실제 송수신만. 제목이 곧 한 줄. */
  const logWire = (
    dir: "◀" | "▶" | "✗",
    stageOrLine: string,
    kind: string,
    data = "",
  ) => {
    let stage = stageOrLine;
    if (
      stage === "시리얼선" ||
      stage === "S/N선" ||
      stage === "S/N" ||
      stage === "serial" ||
      stage === "sn"
    ) {
      stage = "S/N";
    } else if (stage === "MAC선" || stage === "MAC" || stage === "mac") {
      stage = "MAC";
    } else if (
      stage === "DIK" ||
      stage === "dik" ||
      kind === "DIK" ||
      kind === "ACK"
    ) {
      stage = "DIK";
    }

    const type: LogGroupType =
      stage === "DIK" || kind === "DIK" || kind === "ACK"
        ? "dik"
        : stage === "MAC"
          ? "mac"
          : "serial";

    let text = "";
    if (stage === "DIK" && kind === "DIK") {
      text = data ? `${dir} DIK  ${data}` : `${dir} DIK`;
    } else if (data) {
      text = `${dir} ${stage}  ${kind}  ${data}`;
    } else {
      text = `${dir} ${stage}  ${kind}`;
    }

    const saved = activeGroupIdRef.current;
    startLogGroup(type, text);
    endActiveGroup();
    activeGroupIdRef.current = saved;
  };

  /** 주입 성공 결과 한 줄 요약 로그 (○) */
  const logSuccess = (type: LogGroupType, text: string) => {
    const saved = activeGroupIdRef.current;
    startLogGroup(type, text);
    endActiveGroup();
    activeGroupIdRef.current = saved;
  };

  useEffect(() => {
    const el = logScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logGroups]);

  const resetA1Announce = () => {
    a1AnnouncedRef.current = false;
    autoPendingRef.current = false;
    setA1Ready(false);
  };

  const resetSerialSessionState = () => {
    resetA1Announce();
    snRxBufferRef.current = [];
    macRxBufferRef.current = [];
    dikIgnoreRxUntilRef.current = 0;
    lastDikFailAtRef.current = 0;
  };

  const getDikLine = () =>
    isDikLineEnvironment(workEnvironmentRef.current?.dikYn);

  const syncSessionPhaseFromTerminal = (
    row: Pick<
      TerminalInjectInfo,
      | "spSerialInjectionYn"
      | "refInjectionYn"
      | "macInjectionYn"
      | "securityKeyRef1"
      | "securityKeyRef2"
    >,
  ) => {
    const phase = deriveSessionPhase(row, getDikLine());
    setSessionPhase(phase);
    return phase;
  };

  useEffect(() => {
    return () => {
      void closePort();
    };
  }, []);

  useEffect(() => {
    axiosInstance
      .get("/api/v1/terminal/group/list")
      .then((res) => {
        const list = (res.data?.data ?? []) as TerminalGroupOption[];
        setTerminalGroups(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error(err);
        setTerminalGroups([]);
      });
  }, []);

  /** 바코드: 숨김 input 강제 포커스 없이 window 키로 흡수 (드래그 선택 가능) */
  useEffect(() => {
    const isTypingTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false;
      if (target instanceof HTMLInputElement) return true;
      if (target instanceof HTMLTextAreaElement) return true;
      if (target.isContentEditable) return true;
      return false;
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (isTypingTarget(e.target)) return;
      if (e.repeat && e.key !== "Backspace") return;
      if (e.key === "Enter") {
        const raw = scanBufferRef.current;
        scanBufferRef.current = "";
        if (!raw) return;
        e.preventDefault();
        onBarcodeScanRef.current(raw);
        return;
      }
      if (e.key === "Backspace") {
        e.preventDefault();
        scanBufferRef.current = scanBufferRef.current.slice(0, -1);
        return;
      }
      const ch = physicalCharFromKeyboardEvent(e);
      if (!ch) return;
      e.preventDefault();
      scanBufferRef.current += ch;
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const applyTerminalFromRow = (row: TerminalListRow) => {
    const info = mapListRowToInjectInfo(row);
    resetSerialSessionState();
    if (snPortRef.current) {
      setSessionPhase("serial");
    } else {
      syncSessionPhaseFromTerminal(info);
    }
    setTerminal(info);
    terminalRef.current = info;
  };

  const syncSearchFieldsFromSerial = (full: string) => {
    setSearchSerial(full.trim().toUpperCase());
  };

  const showSequenceGap = (
    notice: SequenceSkipNotice | null,
    options?: { silent?: boolean },
  ) => {
    setSequenceGap(notice);
    if (notice && !options?.silent) {
      setGapModal(notice);
    }
  };

  const loadInjectQueue = async (
    fromSpSerialNo: string,
    options?: { silent?: boolean; advanceToNext?: boolean },
  ) => {
    const advance = options?.advanceToNext ?? false;
    // 시리얼 RX 콜백 stale closure 방지: 정렬/그룹은 항상 ref 최신값
    const descending = queueDescendingRef.current;
    const groupIdRaw = selectedTerminalGroupIdRef.current;
    const groupIdNum = groupIdRaw ? Number(groupIdRaw) : NaN;
    const hasGroup = Number.isFinite(groupIdNum) && groupIdNum > 0;

    const inputSn = fromSpSerialNo.trim().toUpperCase();
    // 새 조회일 때만 범위 기준 S/N 갱신. 주입 후 다음 이동 시에는 초기 조회 기준 유지
    if (!advance && inputSn) {
      queueRangeAnchorRef.current = inputSn;
    }
    const q =
      advance && queueRangeAnchorRef.current
        ? queueRangeAnchorRef.current
        : inputSn;

    if (!q && !hasGroup) {
      if (!options?.silent) {
        toast.error("S/N을 입력하세요.");
      }
      return;
    }
    if (q) {
      queueFromRef.current = q;
      setQueueFrom(q);
      if (!advance) {
        syncSearchFieldsFromSerial(q);
      }
    }
    setLoading(true);
    resetA1Announce();
    try {
      const queueParams: Record<string, string | number | boolean> = {
        descending,
        currentPageNo: 1,
        recordsPerPage: INT_MAX,
      };
      if (q) {
        queueParams.fromSpSerialNo = q;
      }
      if (hasGroup) {
        queueParams.terminalGroupId = groupIdNum;
      }
      applyWorkEnvironmentToParams(queueParams, workEnvironmentRef.current);

      let anchor: TerminalListRow | null = null;
      let list: TerminalListRow[] = [];

      if (q) {
        const listParams: Record<string, string | number> = {
          spSerialNo: q,
          currentPageNo: 1,
          recordsPerPage: 1,
        };
        applyWorkEnvironmentToParams(listParams, workEnvironmentRef.current);
        const [anchorRes, queueRes] = await Promise.all([
          axiosInstance.get("/api/v1/terminal/list", {
            params: listParams,
          }),
          axiosInstance.get("/api/v1/terminal/inject-queue", {
            params: queueParams,
          }),
        ]);

        const anchorList = (
          anchorRes.data?.data as { list?: TerminalListRow[] }
        )?.list;
        anchor = anchorList?.[0] ?? null;
        if (!anchor) {
          setTerminal(null);
          setAnchorRow(null);
          setPendingList([]);
          if (!options?.silent) {
            toast.error(`등록되지 않은 S/N: ${q}`);
          }
          return;
        }
        if (
          isWorkEnvironmentModelMismatch(anchor, workEnvironmentRef.current)
        ) {
          setTerminal(null);
          setAnchorRow(null);
          setPendingList([]);
          if (!options?.silent) {
            toast.error(WORK_ENV_MODEL_MISMATCH_MSG);
          }
          return;
        }

        const data = queueRes.data?.data as
          | { list?: TerminalListRow[]; totalCount?: number }
          | undefined;
        const prefix = parseSerialSequence(q)?.prefix ?? "";
        list = filterRowsByPrefix(data?.list ?? [], prefix);

        if (hasGroup && !list.some((row) => row.spSerialNo === q)) {
          setTerminal(null);
          setAnchorRow(null);
          setPendingList([]);
          if (!options?.silent) {
            toast.error(`선택한 Note에 없는 S/N: ${q}`);
          }
          return;
        }
      } else {
        const queueRes = await axiosInstance.get(
          "/api/v1/terminal/inject-queue",
          { params: queueParams },
        );
        const data = queueRes.data?.data as
          | { list?: TerminalListRow[]; totalCount?: number }
          | undefined;
        list = data?.list ?? [];
        const ordered = sortSerialRows(list, descending);
        anchor = ordered[0] ?? null;
        if (!anchor) {
          setTerminal(null);
          setAnchorRow(null);
          setPendingList([]);
          if (!options?.silent) {
            toast.error("선택한 Note에 등록된 단말기가 없습니다.");
          }
          return;
        }
        queueFromRef.current = anchor.spSerialNo;
        queueRangeAnchorRef.current = anchor.spSerialNo;
        setQueueFrom(anchor.spSerialNo);
        syncSearchFieldsFromSerial(anchor.spSerialNo);
      }

      setAnchorRow(anchor);
      const navForWork = buildNavQueue(anchor, list);
      const orderedNavForWork = sortSerialRows(navForWork, descending);
      const pendingCount = countPendingInject(orderedNavForWork);
      setPendingList(list);

      let workRow = anchor;
      if (advance) {
        const completedSn = lastCompletedSerialRef.current;
        workRow =
          findNextIncompleteRow(orderedNavForWork, completedSn) ??
          findNextIncompleteRow(orderedNavForWork, null) ??
          anchor;
      }
      applyTerminalFromRow(workRow);
      if (workRow?.spSerialNo) {
        queueFromRef.current = workRow.spSerialNo;
        setQueueFrom(workRow.spSerialNo);
        syncSearchFieldsFromSerial(workRow.spSerialNo);
      }

      const orderedDisplay = orderedNavForWork;
      const workIdx = orderedDisplay.findIndex(
        (row) => row.spSerialNo === workRow.spSerialNo,
      );
      const nextInList =
        workIdx >= 0 ? orderedDisplay[workIdx + 1] : orderedDisplay[0];
      const listGapNotice = nextInList
        ? buildSequenceSkipNotice(
          workRow.spSerialNo,
          nextInList.spSerialNo,
          orderedDisplay,
        )
        : null;

      if (advance && lastCompletedSerialRef.current) {
        const advanceGapNotice = buildSequenceSkipNotice(
          lastCompletedSerialRef.current,
          workRow.spSerialNo,
          orderedNavForWork,
        );
        showSequenceGap(advanceGapNotice || listGapNotice, {
          silent: options?.silent,
        });
      } else {
        showSequenceGap(listGapNotice, { silent: options?.silent });
      }

      if (!options?.silent) {
        const lookupLabel = q || "Note";
        const anchorDone = isInjectWorkComplete(anchor);
        const orderLabel = descending ? "내림차순" : "오름차순";
        if (anchorDone && pendingCount === 0 && !advance) {
          toast.info(`검색 ${lookupLabel} 주입완료 · 대기 단말 없음`);
        } else if (advance) {
          toast.success(
            `다음 작업 ${workRow.spSerialNo} · 목록 ${orderedNavForWork.length}건 · 대기 ${pendingCount}건 (${orderLabel})`,
          );
        } else if (anchorDone) {
          toast.success(
            `검색 ${lookupLabel} · 주입완료 · 목록 ${orderedNavForWork.length}건 · 대기 ${pendingCount}건 (${orderLabel})`,
          );
        } else {
          toast.success(
            `검색 ${lookupLabel} · 작업대상 ${workRow.spSerialNo} · 목록 ${orderedNavForWork.length}건 · 대기 ${pendingCount}건 (${orderLabel})`,
          );
        }
      }
    } catch (e) {
      toast.error("주입 대기 목록 검색 실패");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  loadInjectQueueRef.current = loadInjectQueue;

  const fetchTerminal = () => {
    const q = searchSerial.trim();
    if (!q && !selectedTerminalGroupId) {
      toast.error("S/N을 입력하세요.");
      return;
    }
    void loadInjectQueueRef.current(q ? normalizeBarcodeScan(q) : "", {
      advanceToNext: false,
    });
  };

  const onBarcodeScan = (raw: string) => {
    const v = normalizeBarcodeScan(raw);
    if (!v) return;
    syncSearchFieldsFromSerial(v);
    lastCompletedSerialRef.current = null;
    setSequenceGap(null);
    setGapModal(null);
    void loadInjectQueueRef.current(v, { advanceToNext: false });
  };
  onBarcodeScanRef.current = onBarcodeScan;

  const patchInjectionStatus = async (
    type: "serial" | "mac" | "dik",
    success: boolean,
    term: TerminalInjectInfo,
  ) => {
    const dikDisplay = formatDikDisplay(term.securityKeyRef1);
    const payload: Record<string, unknown> = {
      terminalId: term.terminalId,
      lookupSpSerialNo: term.spSerialNo,
      message:
        type === "serial"
          ? `S/N 주입 ${success ? "성공" : "실패"}(${term.spSerialNo})`
          : type === "mac"
            ? `MAC 주입 ${success ? "성공" : "실패"}(${term.macAddress})`
            : `DIK 주입 ${success ? "성공" : "실패"}${dikDisplay ? `(${dikDisplay})` : ""}`,
    };
    if (type === "serial") {
      payload.spSerialInjectionYn = success ? "Y" : "N";
    } else if (type === "mac") {
      payload.macInjectionYn = success ? "Y" : "N";
    } else {
      payload.refInjectionYn = success ? "Y" : "N";
    }
    const res = await axiosInstance.patch("/api/v1/terminal/update", payload);
    if (res.data?.code !== "200") {
      throw new Error(res.data?.msg || "서버 갱신 실패");
    }

    const typeLabel =
      type === "serial" ? "S/N" : type === "mac" ? "MAC" : "DIK";
    void setActionLog({
      eventType: "KEY_INJECT",
      title: `${typeLabel} 주입 ${success ? "성공" : "실패"} (${term.spSerialNo || ""})`,
      serialNo: term.spSerialNo || term.serialNo || undefined,
      content: {
        terminalId: term.terminalId,
        spSerialNo: term.spSerialNo,
        macAddress: term.macAddress,
        internalModelName: term.internalModelName,
        externalModelName: term.externalModelName,
        injectType: type,
        result: success ? "SUCCESS" : "FAILURE",
      },
    });
  };

  const refreshTerminalState = (
    patch: Partial<TerminalInjectInfo>,
    options?: { sessionPhase?: SessionPhase },
  ) => {
    setTerminal((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      terminalRef.current = next;
      if (options?.sessionPhase) {
        setSessionPhase(options.sessionPhase);
      } else {
        syncSessionPhaseFromTerminal(next);
      }
      resetA1Announce();
      return next;
    });
  };

  const startReadingDebug = async (
    parser: SerialParser,
    port: SerialPort,
    timeoutMs: number,
  ): Promise<{
    payload: number[] | null;
    raw: number[];
    timedOut: boolean;
    error?: string;
  }> => {
    if (!port.readable) {
      return {
        payload: null,
        raw: [],
        timedOut: false,
        error: "Reader 생성 실패",
      };
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const raw: number[] = [];
    const recvBuffer: number[] = [];
    let payload: number[] | null = null;
    let timedOut = false;
    let resolved = false;

    const timeout = window.setTimeout(() => {
      if (resolved) return;
      timedOut = true;
      abortController.abort();
      const active = serialReceiveRef.current;
      if (active) {
        try {
          void active.cancel();
        } catch {
          /* ignore */
        }
      }
      parser.reset?.();
    }, timeoutMs);

    try {
      const deadline = Date.now() + timeoutMs;
      while (!resolved && !timedOut && Date.now() < deadline) {
        const stream = port.readable;
        if (!stream) break;
        if (stream.locked) {
          await delayMs(40);
          continue;
        }
        let current: ReadableStreamDefaultReader<Uint8Array> | null = null;
        try {
          current = stream.getReader();
        } catch {
          await delayMs(40);
          continue;
        }
        serialReceiveRef.current = current;
        try {
          while (!resolved && !timedOut && Date.now() < deadline) {
            const { value, done } = await current.read();
            if (done) break;
            if (!value) continue;
            const chunk = Array.from(value);
            raw.push(...chunk);
            appendToActiveGroup(
              `◀ RX ${chunk.length}B  ${bytesToHex(chunk)}`,
              "detail",
            );
            appendToActiveGroup(`   ASCII "${bytesToAscii(chunk)}"`, "detail");
            const scanned = scanA3Payload(raw);
            if (scanned) {
              payload = scanned;
              resolved = true;
              break;
            }
            for (const byte of chunk) {
              const result = parser(byte, recvBuffer);
              if (result) {
                payload = result;
                resolved = true;
                break;
              }
            }
          }
        } finally {
          try {
            current.releaseLock();
          } catch {
            /* ignore */
          }
          if (serialReceiveRef.current === current) {
            serialReceiveRef.current = null;
          }
        }
        if (!resolved && !timedOut) {
          await delayMs(60);
        }
      }
    } catch (err: unknown) {
      const name =
        err && typeof err === "object" && "name" in err
          ? String((err as { name: string }).name)
          : "";
      const msg = String(err);
      if (
        name !== "AbortError" &&
        !msg.includes("Releasing") &&
        !msg.includes("BreakError")
      ) {
        return { payload: null, raw, timedOut, error: msg };
      }
    } finally {
      window.clearTimeout(timeout);
      parser.reset?.();
      serialReceiveRef.current = null;
    }
    return { payload, raw, timedOut };
  };

  const waitA3ViaMonitor = (
    expectPayload: string,
    timeoutMs: number,
  ): Promise<number[] | null> =>
    new Promise((resolve) => {
      const expect = expectPayload.replace(/\s/g, "").toUpperCase();
      const targetBuf = isSinglePort
        ? snRxBufferRef.current
        : macRxBufferRef.current;
      const existing = scanA3Payload(targetBuf);
      if (existing) {
        const ascii = bytesToAscii(existing).replace(/\s/g, "").toUpperCase();
        if (ascii.includes(expect)) {
          resolve(existing);
          return;
        }
      }
      const timer = window.setTimeout(() => {
        if (a3WaiterRef.current) {
          a3WaiterRef.current = null;
          resolve(null);
        }
      }, timeoutMs);
      a3WaiterRef.current = {
        expect,
        buffer: [...targetBuf],
        resolve: (found) => {
          window.clearTimeout(timer);
          a3WaiterRef.current = null;
          resolve(found);
        },
      };
    });

  const applyLateMacAck = (
    found: number[],
    pending: NonNullable<typeof pendingMacAckRef.current>,
  ) => {
    pendingMacAckRef.current = null;
    if (a3WaiterRef.current) {
      a3WaiterRef.current.resolve(found);
      return;
    }
    const ascii = bytesToAscii(found);
    void (async () => {
      try {
        await patchInjectionStatus("mac", true, pending.term);
        refreshTerminalState({ macInjectionYn: "Y" });
        lastCompletedSerialRef.current = pending.term.spSerialNo;
        logWire(
          "◀",
          "MAC",
          "A3",
          ascii.replace(/\s/g, "").toUpperCase(),
        );
        logSuccess(
          "mac",
          `○ MAC 주입 성공 ${ascii.replace(/\s/g, "").toUpperCase()}`,
        );
        toast.success("MAC 주입 성공 (지연 응답)");
      } catch {
        /* ignore */
      }
    })();
  };

  const sendA2Payload = async (
    port: SerialPort,
    payload: string,
    line: string,
  ) => {
    const writer = port.writable?.getWriter();
    if (!writer) throw new Error("쓰기 스트림을 가져올 수 없습니다");
    try {
      const packet = buildStxEtxAsciiLenPacket(0xa2, payload);
      await writer.write(packet);
      logWire("▶", line, "A2", payload.replace(/\s/g, "").toUpperCase());
      appendToActiveGroup(`PACKET ${bytesToHex(packet)}`, "detail");
    } finally {
      writer.releaseLock();
    }
  };

  const tryReadA3 = async (
    port: SerialPort,
    expectPayload: string,
    line: string,
  ): Promise<{ ok: boolean; reason?: string }> => {
    const waitMs = 8000;
    try {
      const unlocked = await waitUntilReadableUnlocked(port, 1000);
      if (!unlocked) {
        appendToActiveGroup("✗ 스트림 lock 해제 타임아웃", "detail");
        return { ok: false, reason: "포트 잠금 해제 타임아웃" };
      }
      appendToActiveGroup(
        `A3 대기 ${waitMs}ms · 파서=parseSpSnType1Step2 (STX+A3 프레임)`,
        "detail",
      );
      const { payload, raw, timedOut, error } = await startReadingDebug(
        parseSpSnType1Step2(),
        port,
        waitMs,
      );
      if (error) {
        appendToActiveGroup(`✗ 수신 오류: ${error}`, "detail");
        return { ok: false, reason: `수신 오류(${error})` };
      }
      appendToActiveGroup(
        `수신 합계 ${raw.length}B  HEX ${raw.length ? bytesToHex(raw) : "(없음)"}`,
        "detail",
      );
      if (raw.length) {
        appendToActiveGroup(`수신 ASCII "${bytesToAscii(raw)}"`, "detail");
      }
      if (!payload) {
        if (timedOut) {
          appendToActiveGroup("✗ A3 프레임 미완성/타임아웃", "detail");
          appendToActiveGroup(
            "힌트: 바이트가 없으면 단말이 아직 수신 모드가 아님",
            "detail",
          );
          return { ok: false, reason: "응답 타임아웃" };
        } else {
          appendToActiveGroup("✗ A3 파싱 실패 (STX/LEN/ETX/LRC 불일치)", "detail");
          return { ok: false, reason: "패킷 파싱 실패" };
        }
      }
      const ascii = bytesToAscii(payload);
      appendToActiveGroup(`◀ A3 payload "${ascii}"  HEX ${bytesToHex(payload)}`, "detail");
      if (ascii.includes(expectPayload.replace(/\s/g, ""))) {
        logWire("◀", line, "A3", ascii.replace(/\s/g, "").toUpperCase());
        appendToActiveGroup("✓ 확인 일치", "detail");
        return { ok: true };
      }
      appendToActiveGroup(`✗ 응답 불일치 (기대: ${expectPayload}, 수신: ${ascii})`, "detail");
      return { ok: false, reason: `응답 불일치(수신: ${ascii.replace(/\s/g, "")})` };
    } catch (err) {
      const reason = String(err).replace(/^Error:\s*/, "");
      appendToActiveGroup(`✗ A3 수신 실패: ${reason}`, "detail");
      return { ok: false, reason: reason.includes("타임아웃") ? "응답 타임아웃" : `수신 실패(${reason})` };
    }
  };

  const runInject = async (
    type: "serial" | "mac",
    a1Frame?: number[],
    workTerminal?: TerminalInjectInfo,
    options?: { resumeMonitor?: boolean; portRole?: PortRole },
  ): Promise<boolean> => {
    if (!canWrite) {
      toast.error("실행 권한이 없습니다.");
      return false;
    }
    if (isBusyRef.current) {
      toast.info("이미 주입 작업이 진행 중입니다.");
      return false;
    }
    const term = workTerminal ?? terminalRef.current;
    if (!term) {
      toast.error("단말기를 먼저 검색하세요.");
      return false;
    }
    const role: PortRole =
      type === "serial"
        ? "sn"
        : (options?.portRole ??
          (macPortRef.current ? "mac" : "sn"));
    const port = role === "mac" ? macPortRef.current : snPortRef.current;
    if (!port) {
      toast.error(
        type === "mac"
          ? "MAC을 넣을 시리얼을 연결하세요."
          : "S/N·DIK 선을 연결하세요.",
      );
      return false;
    }
    const monitorRef =
      role === "mac" ? macMonitorReaderRef : snMonitorReaderRef;

    const payload =
      type === "serial"
        ? term.spSerialNo
        : (term.macAddress ?? "").replace(/\s/g, "").toUpperCase();

    if (!payload) {
      toast.error(type === "mac" ? "MAC이 없습니다." : "S/N이 없습니다.");
      return false;
    }

    isBusyRef.current = true;
    const stageLabel = type === "serial" ? "S/N" : "MAC";
    const title =
      type === "serial" ? `${payload} · S/N 주입` : `${payload} · MAC 주입`;

    sendServerDebugLog("runInject 시작", {
      type,
      role,
      payload,
      terminal: term.spSerialNo,
    });

    startLogGroup(type, title);
    appendToActiveGroup(
      `프로토콜: mes_scspro A2→A3 · A1사전=${a1Frame ? "있음" : "없음"}`,
      "detail",
    );
    if (a1Frame) {
      appendToActiveGroup(`◀ A1 수신  ${bytesToHex(a1Frame)}`, "detail");
      appendToActiveGroup(`   ASCII "${bytesToAscii(a1Frame)}"`, "detail");
    } else {
      appendToActiveGroup(
        "버튼 주입: A1 없이 A2 전송. 단말이 먼저 버튼을 눌러 대기 중이어야 함",
        "detail",
      );
    }

    if (type === "serial") {
      await stopSerialRxMonitor(monitorRef);
      setSnListening(false);
    }

    try {
      if (type === "serial") {
        const unlocked = await waitUntilReadableUnlocked(port, 1500);
        if (!unlocked) {
          appendToActiveGroup("✗ 스트림 lock 해제 타임아웃", "detail");
          logSuccess("serial", `X S/N 주입 실패 ${payload} — 포트 잠금 해제 타임아웃`);
          endActiveGroup();
          return false;
        }
      }
      await sendA2Payload(port, payload, stageLabel);
      lastA2SentAtRef.current = Date.now();
      if (type === "mac") {
        pendingMacAckRef.current = {
          expect: payload.replace(/\s/g, "").toUpperCase(),
          term,
          until: Date.now() + 15000,
        };
      }
      let ok = false;
      let failReason = "";
      if (type === "mac") {
        appendToActiveGroup(
          isSinglePort
            ? "A3 대기 12s · 시리얼 감시 유지 (지연·잡음 응답 포함)"
            : "A3 대기 12s · MAC선 감시 유지 (지연·잡음 응답 포함)",
          "detail",
        );
        if (isSinglePort) {
          if (!snMonitorReaderRef.current) {
            startMonitor("sn", true);
          }
        } else {
          if (!macMonitorReaderRef.current) {
            startMonitor("mac", true);
          }
        }
        const a3 = await waitA3ViaMonitor(payload, 12000);
        if (a3) {
          const ascii = bytesToAscii(a3);
          appendToActiveGroup(
            `◀ A3 payload "${ascii}"  HEX ${bytesToHex(a3)}`,
            "detail",
          );
          ok = ascii.replace(/\s/g, "").toUpperCase().includes(
            payload.replace(/\s/g, "").toUpperCase(),
          );
          if (ok) {
            logWire("◀", stageLabel, "A3", ascii.replace(/\s/g, "").toUpperCase());
            appendToActiveGroup("✓ 확인 일치", "detail");
          } else {
            failReason = `응답 불일치(수신: ${ascii.replace(/\s/g, "")})`;
            appendToActiveGroup(`✗ 응답 불일치`, "detail");
          }
        } else {
          failReason = "응답 타임아웃";
          appendToActiveGroup("✗ A3 프레임 미완성/타임아웃", "detail");
        }
      } else {
        const a3Res = await tryReadA3(port, payload, stageLabel);
        ok = a3Res.ok;
        failReason = a3Res.reason ?? "수신 실패";
      }
      if (!ok && type === "mac") {
        appendToActiveGroup("A3 미확인 · 후행 수신 확인 2.5s", "detail");
        await delayMs(2500);
        const targetBuf = isSinglePort
          ? snRxBufferRef.current
          : macRxBufferRef.current;
        const late = scanA3Payload(targetBuf);
        const lateAscii = late ? bytesToAscii(late).replace(/\s/g, "") : "";
        const expect = payload.replace(/\s/g, "");
        if (lateAscii.toUpperCase().includes(expect.toUpperCase())) {
          logWire(
            "◀",
            stageLabel,
            "A3",
            lateAscii.toUpperCase(),
          );
          ok = true;
          failReason = "";
        }
      }
      sendServerDebugLog("runInject 결과", {
        type,
        ok,
        failReason,
        payload,
      });
      if (ok) {
        await patchInjectionStatus(type, true, term);
        if (type === "serial") {
          refreshTerminalState({ spSerialInjectionYn: "Y" }, { sessionPhase: "dik" });
          dikIgnoreRxUntilRef.current = Date.now() + 800;
          logSuccess("serial", `○ S/N 주입 성공 ${payload}`);
        } else {
          refreshTerminalState({ macInjectionYn: "Y" });
          pendingMacAckRef.current = null;
          lastCompletedSerialRef.current = term.spSerialNo;
          resetSerialSessionState();
          logSuccess("mac", `○ MAC 주입 성공 ${payload}`);
          void loadInjectQueueRef.current(
            queueRangeAnchorRef.current ||
            queueFromRef.current ||
            term.spSerialNo,
            { silent: false, advanceToNext: true },
          );
        }
      } else {
        await patchInjectionStatus(type, false, term);
        const suffix = failReason ? ` — ${failReason}` : "";
        if (type === "serial") {
          logSuccess("serial", `X S/N 주입 실패 ${payload}${suffix}`);
        } else {
          logSuccess("mac", `X MAC 주입 실패 ${payload}${suffix}`);
        }
      }
      endActiveGroup();
      return ok;
    } catch (err) {
      const errStr = String(err).replace(/^Error:\s*/, "");
      const reason = errStr.includes("타임아웃") || errStr.includes("timeout")
        ? "응답 타임아웃"
        : errStr;
      sendServerDebugLog("runInject 예외 발생", {
        type,
        errStr,
        reason,
      });
      appendToActiveGroup(`✗ 전송 실패: ${errStr}`, "detail");
      if (type === "serial") {
        logSuccess("serial", `X S/N 주입 실패 ${payload} — ${reason}`);
      } else {
        logSuccess("mac", `X MAC 주입 실패 ${payload} — ${reason}`);
      }
      endActiveGroup();
      return false;
    } finally {
      a3WaiterRef.current = null;
      isBusyRef.current = false;
      resetA1Announce();
      if (options?.resumeMonitor !== false) {
        startMonitor(role, true);
      }
    }
  };

  const runDikInject = async (
    workTerminal?: TerminalInjectInfo,
    options?: { resumeMonitor?: boolean },
  ): Promise<boolean> => {
    if (!canWrite) {
      toast.error("실행 권한이 없습니다.");
      return false;
    }
    if (isBusyRef.current) {
      toast.info("이미 주입 작업이 진행 중입니다.");
      return false;
    }
    isBusyRef.current = true;
    const term = workTerminal ?? terminalRef.current;
    if (!term) {
      toast.error("단말기를 먼저 검색하세요.");
      return false;
    }
    const port = snPortRef.current;
    if (!port) {
      toast.error(
        isSinglePort ? "시리얼 포트를 연결하세요." : "S/N·DIK 선을 연결하세요.",
      );
      return false;
    }
    const dikSn = (term.securityKeyRef1 ?? "").trim();
    const dik = (term.securityKeyRef2 ?? "").trim();
    if (!hasRegisteredDik(term)) {
      toast.error("등록된 DIK가 없습니다.");
      isBusyRef.current = false;
      return false;
    }
    if (!dikSn || !dik) {
      toast.error("DIK 정보가 올바르지 않습니다.");
      isBusyRef.current = false;
      return false;
    }

    let dikSucceeded = false;
    const dikLineLabel = "DIK";
    const dikSummary = formatDikSuccessSummary(dikSn, dik);
    startLogGroup("dik", "DIK 주입");
    appendToActiveGroup("DIK 패킷 전송", "detail");
    sendServerDebugLog("runDikInject 시작", { dikSn, dikSummary });
    await stopSerialRxMonitor(snMonitorReaderRef);
    setSnListening(false);

    try {
      const unlocked = await waitUntilReadableUnlocked(port, 1000);
      if (!unlocked) {
        appendToActiveGroup("✗ 스트림 lock 해제 타임아웃", "detail");
        sendServerDebugLog("runDikInject 실패: 포트 잠금 해제 타임아웃", {
          dikSn,
        });
        await patchInjectionStatus("dik", false, term);
        logSuccess(
          "dik",
          `X DIK 주입 실패 ${dikSummary} — 포트 잠금 해제 타임아웃`,
        );
        endActiveGroup();
        return false;
      }

      const packet = buildDikPacket(dikSn, dik);
      const writer = port.writable?.getWriter();
      if (!writer) throw new Error("쓰기 스트림을 가져올 수 없습니다");
      try {
        await writer.write(packet);
      } finally {
        writer.releaseLock();
      }
      sendServerDebugLog("DIK 패킷 전송 완료", { dikSn, dikSummary });
      logWire("▶", dikLineLabel, "DIK", dikSummary);
      appendToActiveGroup("▶ DIK 전송", "detail");

      const parser = parseDikType1();
      const res2 = await startReading(
        parser,
        port,
        10000,
        serialReceiveRef,
        abortControllerRef,
      );
      appendToActiveGroup(`◀ DIK 응답  ${bytesToHex(res2)}`, "detail");
      sendServerDebugLog("DIK 응답 수신", {
        res2: bytesToHex(res2),
        ack: res2[0],
      });
      if (res2[0] !== 0) {
        appendToActiveGroup("✗ DIK 응답 실패", "detail");
        await patchInjectionStatus("dik", false, term);
        logSuccess(
          "dik",
          `X DIK 주입 실패 ${dikSummary} — 응답 오류(ACK 0x${bytesToHex([res2[0]])})`,
        );
        endActiveGroup();
        return false;
      }

      appendToActiveGroup(
        "DIK ACK 성공 · NFC_READY는 읽지 않음 (키주입 화면 유지)",
        "detail",
      );

      await patchInjectionStatus("dik", true, term);
      refreshTerminalState({ refInjectionYn: "Y" }, { sessionPhase: "done" });
      logWire("◀", dikLineLabel, "ACK", "00");
      appendToActiveGroup("✓ DIK 주입 성공", "detail");
      logSuccess(
        "dik",
        `○ DIK 주입 성공 ${dikSummary}`,
      );
      endActiveGroup();
      dikSucceeded = true;
      sendServerDebugLog("runDikInject 성공 완료", { dikSn });
      return true;
    } catch (err) {
      const errStr = String(err).replace(/^Error:\s*/, "");
      const reason = errStr.includes("타임아웃") || errStr.includes("timeout")
        ? "응답 타임아웃"
        : errStr;
      sendServerDebugLog("runDikInject 예외 발생", { errStr, reason });
      appendToActiveGroup(`✗ DIK 실패: ${errStr}`, "detail");
      try {
        await patchInjectionStatus("dik", false, term);
      } catch {
        /* ignore */
      }
      logSuccess(
        "dik",
        `X DIK 주입 실패 ${dikSummary} — ${reason}`,
      );
      endActiveGroup();
      return false;
    } finally {
      isBusyRef.current = false;
      resetA1Announce();
      if (options?.resumeMonitor !== false) {
        startMonitor("sn", true);
      }
      if (dikSucceeded && !isSinglePort) {
        void kickMacRx("DIK 완료 · MAC 대기");
      }
    }
  };

  const notifyWebInjectStep = (
    step: "serial" | "dik" | "mac",
    frame: number[],
  ) => {
    setA1Ready(true);
    const stageLabel = step === "serial" ? "S/N" : step === "dik" ? "DIK" : "MAC";
    const label =
      step === "serial"
        ? "S/N 전송(F4)"
        : step === "dik"
          ? "DIK 전송(F8)"
          : "MAC 전송(F9)";
    if (a1AnnouncedRef.current) return;
    a1AnnouncedRef.current = true;
    logWire("◀", stageLabel, "A1", bytesToHex(frame));
    toast.info(`A1 수신 · 웹에서 ${label}을 누르세요.`);
  };

  const handleA1Frame = (frame: number[], source: PortRole) => {
    const workTerminal = terminalRef.current;
    if (!workTerminal) {
      sendServerDebugLog("A1 수신했으나 작업 단말 미선택 상태", {
        source,
      });
      if (!a1AnnouncedRef.current) {
        a1AnnouncedRef.current = true;
        const stage = isSinglePort ? "S/N" : source === "mac" ? "MAC" : "S/N";
        logSystem(`A1 수신(${stage}) — 작업 단말을 먼저 검색하세요`, "err");
        toast.warn("S/N 검색 후 단말기에서 버튼을 누르세요.");
      }
      return;
    }
    if (isBusyRef.current || autoPendingRef.current) {
      sendServerDebugLog("A1 수신했으나 Busy 상태로 무시됨", {
        isBusy: isBusyRef.current,
        autoPending: autoPendingRef.current,
      });
      return;
    }
    const now = Date.now();
    if (source === "sn" && now < dikIgnoreRxUntilRef.current) {
      return;
    }
    if (
      source === "sn" &&
      sessionPhaseRef.current === "serial" &&
      now - lastA2SentAtRef.current < 400
    ) {
      return;
    }

    const isAuto = injectModeRef.current === "auto";
    const workPhase = sessionPhaseRef.current;

    const scheduleAuto = (step: "serial" | "mac", sendRole: PortRole) => {
      sendServerDebugLog("scheduleAuto 트리거", {
        step,
        sendRole,
        workPhase,
        terminal: workTerminal.spSerialNo,
      });
      autoPendingRef.current = true;
      const stage = step === "serial" ? "S/N" : "MAC";
      if (!a1AnnouncedRef.current) {
        logWire("◀", stage, "A1", bytesToHex(frame));
      }
      a1AnnouncedRef.current = true;
      setA1Ready(true);
      const label = step === "serial" ? "S/N" : "MAC";
      window.setTimeout(() => {
        void (async () => {
          try {
            if (!isSinglePort) {
              const port =
                sendRole === "mac" ? macPortRef.current : snPortRef.current;
              if (port) {
                await waitUntilReadableUnlocked(port, 1500);
              }
            }
            const ok = await runInject(step, frame, workTerminal, {
              portRole: sendRole,
            });
            toast[ok ? "success" : "error"](
              ok ? `${label} 자동 전송 성공` : `${label} 자동 전송 실패`,
            );
            if (ok && step === "serial") {
              toast.info("다음: 단말기 DIK 버튼 후 웹 DIK 전송(F8)");
            }
          } finally {
            autoPendingRef.current = false;
          }
        })();
      }, isSinglePort ? 40 : 80);
    };

    // 단일 포트 모드
    if (isSinglePort) {
      if (workPhase === "done") {
        if (a3WaiterRef.current || pendingMacAckRef.current) return;
        if (now - lastA2SentAtRef.current < 800) return;
        if (isAuto) {
          scheduleAuto("mac", "sn");
          return;
        }
        notifyWebInjectStep("mac", frame);
        return;
      }
      if (workPhase === "dik") {
        notifyWebInjectStep("dik", frame);
        return;
      }
      if (isAuto) scheduleAuto("serial", "sn");
      else notifyWebInjectStep("serial", frame);
      return;
    }

    // 기존 2포트 모드:
    // S/N선 A1은 항상 S/N 또는 DIK. MAC은 MAC선에서만.
    if (source === "sn") {
      if (workPhase === "dik") {
        notifyWebInjectStep("dik", frame);
        return;
      }
      if (workPhase !== "serial") {
        setSessionPhase("serial");
      }
      if (isAuto) scheduleAuto("serial", "sn");
      else notifyWebInjectStep("serial", frame);
      return;
    }

    // MAC선 A1만 MAC 주입 (재주입 포함. DB 완료여도 단말 버튼이면 다시 보냄)
    if (source === "mac") {
      if (a3WaiterRef.current || pendingMacAckRef.current) return;
      if (now - lastA2SentAtRef.current < 800) return;
      if (isAuto) {
        if (!macPortRef.current) {
          toast.error("MAC선을 연결하세요.");
          return;
        }
        scheduleAuto("mac", "mac");
        return;
      }
      notifyWebInjectStep("mac", frame);
    }
  };

  const lastRxServerLogAtRef = useRef(0);

  const logRxChunk = (bytes: number[], source: PortRole) => {
    if (bytes.length === 0) return;
    const line = isSinglePort
      ? "시리얼선"
      : source === "mac"
        ? "MAC선"
        : "S/N선";
    console.log(
      `[NC-7000-SERIAL-RX][${line}] ${bytes.length}B HEX: ${bytesToHex(bytes)} ASCII: "${bytesToAscii(bytes)}"`,
    );
    const now = Date.now();
    if (now - lastRxServerLogAtRef.current > 1500) {
      lastRxServerLogAtRef.current = now;
      sendServerDebugLog(`시리얼 수신 (${line})`, {
        len: bytes.length,
        hex: bytesToHex(bytes),
        ascii: bytesToAscii(bytes),
      });
    }
    if (!showSerialDetailRef.current) return;
    if (isBusyRef.current && source === "sn") return;
    startLogGroup("system", `시리얼 수신 (${line})`);
    appendToActiveGroup(
      `◀ RX ${bytes.length}B  HEX ${bytesToHex(bytes)}  ASCII "${bytesToAscii(bytes)}"`,
      "detail",
    );
    endActiveGroup();
  };

  const onSnRxChunk = (bytes: number[]) => {
    logRxChunk(bytes, "sn");
    snRxBufferRef.current.push(...bytes);
    if (isSinglePort) {
      if (snRxBufferRef.current.length > 4096) {
        snRxBufferRef.current = snRxBufferRef.current.slice(-2048);
      }
      const pending = pendingMacAckRef.current;
      if (pending && Date.now() > pending.until) {
        pendingMacAckRef.current = null;
      }
      const waiter = a3WaiterRef.current;
      const ack = pendingMacAckRef.current;
      const scanBuf = waiter
        ? waiter.buffer.concat(bytes)
        : snRxBufferRef.current;
      if (waiter) {
        waiter.buffer.push(...bytes);
      }
      const found = scanA3Payload(scanBuf);
      if (found) {
        const ascii = bytesToAscii(found).replace(/\s/g, "").toUpperCase();
        if (waiter && ascii.includes(waiter.expect)) {
          waiter.resolve(found);
          return;
        }
        if (ack && ascii.includes(ack.expect)) {
          applyLateMacAck(found, ack);
          return;
        }
      }
      if (waiter || ack) return;
    }
    const { frames, remaining } = drainA1Frames(snRxBufferRef.current);
    snRxBufferRef.current = remaining;
    if (frames.length > 0) {
      console.log(
        `[NC-7000-SERIAL-A1][S/N선] A1 프레임 ${frames.length}개 감지:`,
        frames.map((f) => bytesToHex(f)),
      );
      sendServerDebugLog("A1 감지 (S/N선)", {
        count: frames.length,
        frames: frames.map((f) => bytesToHex(f)),
        phase: sessionPhaseRef.current,
        terminal: terminalRef.current?.spSerialNo,
      });
    }
    for (const frame of frames) {
      handleA1Frame(frame, "sn");
    }
  };
  onSnRxChunkRef.current = onSnRxChunk;

  const onMacRxChunk = (bytes: number[]) => {
    logRxChunk(bytes, "mac");
    macRxBufferRef.current.push(...bytes);
    if (macRxBufferRef.current.length > 4096) {
      macRxBufferRef.current = macRxBufferRef.current.slice(-2048);
    }
    const pending = pendingMacAckRef.current;
    if (pending && Date.now() > pending.until) {
      pendingMacAckRef.current = null;
    }
    const waiter = a3WaiterRef.current;
    const ack = pendingMacAckRef.current;
    const scanBuf = waiter ? waiter.buffer.concat(bytes) : macRxBufferRef.current;
    if (waiter) {
      waiter.buffer.push(...bytes);
    }
    const found = scanA3Payload(scanBuf);
    if (found) {
      const ascii = bytesToAscii(found).replace(/\s/g, "").toUpperCase();
      if (waiter && ascii.includes(waiter.expect)) {
        waiter.resolve(found);
        return;
      }
      if (ack && ascii.includes(ack.expect)) {
        applyLateMacAck(found, ack);
        return;
      }
    }
    if (waiter || ack) return;
    const { frames, remaining } = drainA1Frames(macRxBufferRef.current);
    macRxBufferRef.current = remaining;
    if (frames.length > 0) {
      console.log(
        `[NC-7000-SERIAL-A1][MAC선] A1 프레임 ${frames.length}개 감지:`,
        frames.map((f) => bytesToHex(f)),
      );
      sendServerDebugLog("A1 감지 (MAC선)", {
        count: frames.length,
        frames: frames.map((f) => bytesToHex(f)),
        phase: sessionPhaseRef.current,
        terminal: terminalRef.current?.spSerialNo,
      });
    }
    for (const frame of frames) {
      handleA1Frame(frame, "mac");
    }
  };
  onMacRxChunkRef.current = onMacRxChunk;

  const startMonitor = (role: PortRole, silent = false) => {
    const port = role === "mac" ? macPortRef.current : snPortRef.current;
    const monitorRef = role === "mac" ? macMonitorReaderRef : snMonitorReaderRef;
    const setListening = role === "mac" ? setMacListening : setSnListening;
    const rxBuf = role === "mac" ? macRxBufferRef : snRxBufferRef;
    const onChunkRef = role === "mac" ? onMacRxChunkRef : onSnRxChunkRef;
    const label = role === "mac" ? "MAC선" : "S/N선";
    if (!port) return;
    const attempt = (n: number) => {
      if (!port) return;
      if (monitorRef.current && !port.readable?.locked) {
        monitorRef.current = null;
      }
      if (monitorRef.current) return;
      if (port.readable?.locked) {
        if (n < 10) {
          window.setTimeout(() => attempt(n + 1), 120);
          return;
        }
        setListening(false);
        if (!silent) logSystem(`${label} A1 감시 시작 실패 · 재연결 필요`, "err");
        return;
      }
      rxBuf.current =
        role === "mac" && (a3WaiterRef.current || pendingMacAckRef.current)
          ? rxBuf.current
          : [];
      const ok = startSerialRxMonitor(
        port,
        (bytes) => onChunkRef.current(bytes),
        monitorRef,
      );
      if (ok) {
        setListening(true);
        if (!silent) logSystem(`${label} A1 감시 시작`, "detail");
      } else if (n < 10) {
        window.setTimeout(() => attempt(n + 1), 120);
      } else {
        setListening(false);
        if (!silent) logSystem(`${label} A1 감시 시작 실패 · 재연결 필요`, "err");
      }
    };
    attempt(0);
  };

  /**
   * USB-UART 가 웹에서 TX 하기 전까지 RX 를 올리지 않는 경우가 있다.
   * MAC 선에 STX 가 아닌 1바이트를 써서 파이프라인을 깨운다.
   */
  const pokeMacWritable = async () => {
    const port = macPortRef.current;
    if (!port?.writable) return;
    try {
      const writer = port.writable.getWriter();
      try {
        await writer.write(new Uint8Array([0x00]));
      } finally {
        writer.releaseLock();
      }
    } catch {
      /* 모니터는 readable 만 쓰므로 writable 충돌은 무시 */
    }
  };

  /** MAC 리더를 떼었다가 다시 붙여, 칩에 쌓인 RX 가 웹으로 쏟아지게 한다. */
  const kickMacRx = async (reason: string, silent = false) => {
    if (!macPortRef.current) return;
    if (a3WaiterRef.current || pendingMacAckRef.current) return;
    await pokeMacWritable();
    await stopSerialRxMonitor(macMonitorReaderRef);
    setMacListening(false);
    await delayMs(80);
    startMonitor("mac", true);
    if (!silent) logSystem(`MAC선 수신 깨움 (${reason})`, "detail");
  };

  const closeOnePort = async (role: PortRole) => {
    const monitorRef = role === "mac" ? macMonitorReaderRef : snMonitorReaderRef;
    await stopSerialRxMonitor(monitorRef);
    if (role === "mac") setMacListening(false);
    else setSnListening(false);
    const port = role === "mac" ? macPortRef.current : snPortRef.current;
    if (port) {
      try {
        await port.close();
      } catch {
        /* ignore */
      }
    }
    if (role === "mac") {
      macPortRef.current = null;
      setIsMacPortOpen(false);
    } else {
      snPortRef.current = null;
      setIsSnPortOpen(false);
    }
  };

  const applyOpenedPort = (
    port: SerialPort,
    reconnect: boolean,
    role: PortRole,
  ) => {
    const label = isSinglePort
      ? "시리얼"
      : role === "mac"
        ? "MAC선"
        : "S/N·DIK선";
    if (role === "mac") {
      macPortRef.current = port;
      setIsMacPortOpen(true);
    } else {
      snPortRef.current = port;
      setIsSnPortOpen(true);
      setSessionPhase("serial");
    }
    resetA1Announce();
    startLogGroup("connect", reconnect ? `${label} 재연결` : `${label} 연결`);
    appendToActiveGroup(
      reconnect
        ? "남은 허용 포트 1개 · 선택 창 생략 · 115200 8N1"
        : "포트 연결 115200 8N1",
      "ok",
    );
    endActiveGroup();
    startMonitor(role);
    if (role === "mac") {
      void (async () => {
        await delayMs(120);
        await kickMacRx(reconnect ? "MAC 재연결" : "MAC 연결");
      })();
    } else if (macPortRef.current) {
      void (async () => {
        await delayMs(150);
        await kickMacRx("S/N 연결 후 MAC 유지");
      })();
    }
    toast.success(
      reconnect ? `${label} 재연결 · A1 감시 시작` : `${label} 연결 · A1 감시 시작`,
    );
  };

  const tryOpenPort = async (port: SerialPort) => {
    try {
      await port.open({ baudRate: 115200, flowControl: "none" });
      return true;
    } catch (err) {
      if (port.readable) return true;
      throw err;
    }
  };

  /**
   * 역할별 시리얼 연결. 두 COM을 동시에 열 수 있다.
   * 다른 역할이 쓰지 않는 허용 포트가 1개면 선택 창 없이 open.
   */
  const openPort = async (role: PortRole) => {
    if (typeof navigator === "undefined" || !navigator.serial) {
      toast.error("이 브라우저는 Web Serial을 지원하지 않습니다. Chrome/Edge를 사용하세요.");
      return;
    }
    const current = role === "mac" ? macPortRef.current : snPortRef.current;
    if (current) {
      toast.info(
        isSinglePort
          ? "시리얼 포트가 이미 연결되어 있습니다"
          : role === "mac"
            ? "MAC 선이 이미 연결되어 있습니다"
            : "S/N·DIK 선이 이미 연결되어 있습니다",
      );
      return;
    }
    const other = isSinglePort
      ? null
      : role === "mac"
        ? snPortRef.current
        : macPortRef.current;

    try {
      const granted = await navigator.serial.getPorts();
      const unused = other ? granted.filter((p) => p !== other) : granted;
      let port: SerialPort | null = null;
      let reconnect = false;

      if (unused.length === 1) {
        port = unused[0];
        reconnect = true;
        try {
          await tryOpenPort(port);
        } catch {
          reconnect = false;
          port = await navigator.serial.requestPort();
          if (other && port === other) {
            toast.error("이미 다른 선으로 연결된 포트입니다. 나머지 COM을 선택하세요.");
            return;
          }
          await tryOpenPort(port);
        }
      } else {
        port = await navigator.serial.requestPort();
        if (other && port === other) {
          toast.error("이미 다른 선으로 연결된 포트입니다. 나머지 COM을 선택하세요.");
          return;
        }
        await tryOpenPort(port);
      }

      applyOpenedPort(port, reconnect, role);
    } catch (err) {
      const name =
        err && typeof err === "object" && "name" in err
          ? String((err as { name: string }).name)
          : "";
      if (name === "NotFoundError" || name === "AbortError") {
        return;
      }
      toast.error(String(err));
    }
  };

  const openNextPort = async () => {
    if (!snPortRef.current) {
      await openPort("sn");
      return;
    }
    if (!isSinglePort && !macPortRef.current) {
      await openPort("mac");
      return;
    }
    toast.info(
      isSinglePort
        ? "시리얼 포트가 이미 연결되어 있습니다"
        : "S/N·DIK 선과 MAC 선이 모두 연결되어 있습니다",
    );
  };

  const closePort = async () => {
    const hadAny = !!(snPortRef.current || macPortRef.current);
    await closeOnePort("sn");
    await closeOnePort("mac");
    resetA1Announce();
    if (hadAny) logSystem("포트 연결 해제", "detail");
  };

  openNextPortRef.current = openNextPort;
  closePortRef.current = closePort;

  // F2 연결 / F3 해제 / F4 S/N / F8 DIK / F9 MAC
  // F5·F6·F7·F11·F12는 브라우저 기본 동작과 겹쳐서 쓰지 않음
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (
        e.key !== "F2" &&
        e.key !== "F3" &&
        e.key !== "F4" &&
        e.key !== "F8" &&
        e.key !== "F9"
      ) {
        return;
      }
      e.preventDefault();
      if (e.key === "F2") {
        void openNextPortRef.current();
        return;
      }
      if (e.key === "F3") {
        if (!snPortRef.current && !macPortRef.current) {
          toast.info("연결된 시리얼이 없습니다");
          return;
        }
        void closePortRef.current();
        return;
      }
      if (e.key === "F4") {
        void onSendSerialRef.current();
        return;
      }
      if (e.key === "F8") {
        void onSendDikRef.current();
        return;
      }
      void onSendMacRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!isMacPortOpen) return;
    let ticks = 0;
    const id = window.setInterval(() => {
      if (a3WaiterRef.current || pendingMacAckRef.current || isBusyRef.current) {
        return;
      }
      ticks += 1;
      if (ticks % 3 === 0) {
        void kickMacRx("keep-alive", true);
      } else {
        void pokeMacWritable();
      }
    }, 1500);
    return () => window.clearInterval(id);
  }, [isMacPortOpen]);

  const onSendSerial = async () => {
    if (!canWrite) {
      toast.error("실행 권한이 없습니다.");
      return;
    }
    if (!snPortRef.current) {
      toast.error(
        isSinglePort ? "시리얼 포트를 연결하세요." : "S/N·DIK 선을 연결하세요.",
      );
      return;
    }
    if (!terminal) {
      toast.error("단말기를 먼저 검색하세요.");
      return;
    }
    const ok = await runInject(
      "serial",
      undefined,
      terminalRef.current ?? undefined,
      { resumeMonitor: true },
    );
    if (!ok) {
      toast.error("S/N 전송 실패");
      return;
    }
    toast.success("S/N 전송 성공");
    toast.info(
      dikStepRequired(terminal, getDikLine())
        ? "다음: 단말기 DIK 버튼 → 웹 DIK 전송(F8)"
        : "다음: 단말기 MAC 버튼 → 웹 MAC 전송(F9)",
    );
  };

  const onSendDik = async () => {
    if (!canWrite) {
      toast.error("실행 권한이 없습니다.");
      return;
    }
    if (!snPortRef.current) {
      toast.error(
        isSinglePort ? "시리얼 포트를 연결하세요." : "S/N·DIK 선을 연결하세요.",
      );
      return;
    }
    if (!terminal) {
      toast.error("단말기를 먼저 검색하세요.");
      return;
    }
    const ok = await runDikInject(terminalRef.current ?? undefined, {
      resumeMonitor: true,
    });
    if (ok) {
      toast.success("DIK 전송 성공");
      toast.info(
        isSinglePort
          ? "다음: 단말기 MAC 버튼 → 웹 MAC 전송(F9)"
          : "다음: MAC선에서 단말기 MAC 버튼 → 웹 MAC 전송(F9)",
      );
    } else {
      toast.error("DIK 전송 실패");
      lastDikFailAtRef.current = Date.now();
    }
  };

  const onSendMac = async () => {
    if (!canWrite) {
      toast.error("실행 권한이 없습니다.");
      return;
    }
    const targetPort = isSinglePort
      ? snPortRef.current
      : (macPortRef.current ?? snPortRef.current);
    if (!targetPort) {
      toast.error(
        isSinglePort ? "시리얼 포트를 연결하세요." : "MAC선을 연결하세요.",
      );
      return;
    }
    if (!terminal?.macAddress?.trim()) {
      toast.error("등록된 MAC이 없습니다.");
      return;
    }
    const ok = await runInject(
      "mac",
      undefined,
      terminalRef.current ?? undefined,
      {
        resumeMonitor: true,
        portRole: isSinglePort ? "sn" : macPortRef.current ? "mac" : "sn",
      },
    );
    if (ok) {
      toast.success("MAC 전송 성공");
    } else {
      toast.error("MAC 전송 실패");
    }
  };

  onSendSerialRef.current = onSendSerial;
  onSendDikRef.current = onSendDik;
  onSendMacRef.current = onSendMac;

  const dikLine = isDikLineEnvironment(workEnvironment?.dikYn);
  const heroHint = (() => {
    if (!terminal) {
      return { kind: "idle" as const, text: "검색 후 전송할 값 미리보기" };
    }
    const dikNeeded = dikStepRequired(terminal, dikLine);
    const pending: string[] = [];
    if (!isInjected(terminal.spSerialInjectionYn)) pending.push("S/N");
    if (dikNeeded && !isInjected(terminal.refInjectionYn)) pending.push("DIK");
    if (!isInjected(terminal.macInjectionYn)) pending.push("MAC");
    if (pending.length) {
      return { kind: "work" as const, need: pending.join("·"), text: "" };
    }
    return {
      kind: "done" as const,
      need: "",
      text: dikNeeded ? "S/N·DIK·MAC 완료" : "S/N·MAC 완료",
    };
  })();
  const anyPortOpen = isSnPortOpen || isMacPortOpen;
  const a1BannerText = (() => {
    const isManual = injectMode === "manual";
    if (!isSnPortOpen && !isMacPortOpen) {
      return "";
    }
    if (isMacPortOpen && sessionPhase === "done") {
      if (isInjected(terminal?.macInjectionYn)) {
        return terminal && dikStepRequired(terminal, dikLine)
          ? "S/N·DIK·MAC 완료"
          : "S/N·MAC 완료";
      }
      if (a1Ready) {
        return isManual
          ? "A1 수신 — F9 또는 MAC 전송"
          : "A1 수신 — MAC 자동 전송 중";
      }
      return isManual
        ? "MAC 전송 대기 — F9 또는 MAC 전송"
        : "MAC 전송 대기 — 단말기에서 MAC 버튼을 누르세요";
    }
    if (!isSnPortOpen) {
      return "S/N·DIK 선을 연결하세요 (F2)";
    }
    if (a1Ready) {
      return sessionPhase === "dik"
        ? "A1 수신 — F8 또는 DIK 전송"
        : "A1 수신 — S/N 자동 전송 중";
    }
    if (sessionPhase === "dik") {
      return "DIK 전송 대기 — 단말기 버튼 후 F8 또는 DIK 전송";
    }
    return isManual
      ? "S/N 전송 대기 — F4 또는 S/N 전송"
      : "S/N 전송 대기 — 단말기에서 1번(S/N)을 누르세요";
  })();
  const displayQueue = useMemo(
    () =>
      sortSerialRows(
        ensureCurrentInQueue(
          buildDisplayQueue(anchorRow, pendingList),
          terminal,
        ),
        queueDescending,
      ),
    [anchorRow, pendingList, queueDescending, terminal],
  );
  const navQueue = useMemo(
    () =>
      sortSerialRows(
        ensureCurrentInQueue(buildNavQueue(anchorRow, pendingList), terminal),
        queueDescending,
      ),
    [anchorRow, pendingList, queueDescending, terminal],
  );
  const navQueueCount = navQueue.length;
  const pendingWorkCount = useMemo(
    () => countPendingInject(navQueue),
    [navQueue],
  );
  const currentNavIndex = useMemo(() => {
    if (!terminal) return -1;
    return navQueue.findIndex((row) => row.spSerialNo === terminal.spSerialNo);
  }, [navQueue, terminal]);
  const nextWorkSn =
    findNextIncompleteRow(navQueue, terminal?.spSerialNo ?? null)?.spSerialNo ??
    null;

  const queueScrollRef = useRef<HTMLDivElement | null>(null);

  const scrollQueueBy = (direction: -1 | 1) => {
    const el = queueScrollRef.current;
    if (!el) return;
    const step = Math.max(160, Math.floor(el.clientWidth * 0.7));
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  // 작업 대상이 바뀌면 목록에서 해당 배지가 보이도록 가로 스크롤
  useEffect(() => {
    const sn = terminal?.spSerialNo?.trim();
    const wrap = queueScrollRef.current;
    if (!sn || !wrap) return;
    const badge = wrap.querySelector<HTMLElement>(
      `[data-queue-sn="${CSS.escape(sn)}"]`,
    );
    badge?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [terminal?.spSerialNo, displayQueue.length]);

  useEffect(() => {
    const currentFrom =
      queueRangeAnchorRef.current.trim() || queueFromRef.current.trim();
    if (!currentFrom && !selectedTerminalGroupId) return;
    void loadInjectQueueRef.current(currentFrom, {
      silent: true,
      advanceToNext: false,
    });
  }, [queueDescending]);

  const goToQueueIndex = (index: number) => {
    const row = navQueue[index];
    if (!row) return;
    lastCompletedSerialRef.current = null;
    applyTerminalFromRow(row);
    const next = navQueue[index + 1];
    const gapNotice = next
      ? buildSequenceSkipNotice(row.spSerialNo, next.spSerialNo, navQueue)
      : null;
    showSequenceGap(gapNotice);
  };

  const goToPrevSerial = () => {
    if (currentNavIndex > 0) goToQueueIndex(currentNavIndex - 1);
  };

  const goToNextSerial = () => {
    if (currentNavIndex >= 0 && currentNavIndex < navQueue.length - 1) {
      goToQueueIndex(currentNavIndex + 1);
    }
  };

  const canGoPrev = currentNavIndex > 0;
  const canGoNext =
    currentNavIndex >= 0 && currentNavIndex < navQueue.length - 1;

  const queueSummaryText = queueFrom
    ? `${queueDescending ? "내림" : "오름"} · ${navQueueCount}건 · 대기 ${pendingWorkCount}`
    : null;

  const lineColor = (tone: LogTone) => {
    if (tone === "ok") return "#15803d";
    if (tone === "err") return "#dc2626";
    if (tone === "detail") return "#64748b";
    return "#1e293b";
  };

  return (
    <div className={classes.page}>
      <div className={classes.topArea}>
        <Box>
          <Text fz="xl" fw={700}>
            {menuTitle || "키주입테스트"}
          </Text>
          {injectMode === "auto" ? (
            <Text size="sm" c="dimmed" mt={4}>
              {isSinglePort
                ? "자동: 단말기 1번 → S/N 자동. DIK는 단말 버튼 후 웹 F8. MAC 버튼 → MAC 자동(F9도 가능)"
                : "자동: 단말기 1번 → S/N 자동. DIK는 단말 버튼 후 웹 F8. MAC선 A1 → MAC 자동(F9도 가능)"}
            </Text>
          ) : (
            <Text size="sm" c="dimmed" mt={4}>
              수동: S/N·DIK·MAC 모두 단말기 버튼 후 웹에서 전송
            </Text>
          )}
        </Box>

        <Paper p="md" withBorder radius="md">
          <Flex gap="sm" align="flex-end" wrap="wrap">
            <Box>
              <Text size="sm" fw={500} mb={4}>
                주입 방식
              </Text>
              <SegmentedControl
                value={injectMode}
                onChange={(value) => {
                  const next = value as InjectMode;
                  setInjectMode(next);
                  a1AnnouncedRef.current = false;
                  setA1Ready(false);
                  logSystem(
                    next === "auto"
                      ? "자동 모드 — S/N·MAC은 A1 자동, DIK는 웹 F8"
                      : "수동 모드 — S/N·DIK·MAC 모두 웹 버튼",
                    "ok",
                  );
                }}
                data={[
                  { label: "자동", value: "auto" },
                  { label: "수동", value: "manual" },
                ]}
                size="md"
              />
            </Box>
            <TextInput
              label={
                <Flex
                  justify="space-between"
                  align="center"
                  gap="sm"
                  wrap="wrap"
                  w="100%"
                >
                  <span>S/N 검색</span>
                  {queueSummaryText && (
                    <Text size="xs" c="dimmed" fw={500}>
                      {queueSummaryText}
                    </Text>
                  )}
                </Flex>
              }
              placeholder="바코드 스캔 또는 직접 입력"
              value={searchSerial}
              onChange={(e) =>
                setSearchSerial(e.currentTarget.value.toUpperCase())
              }
              style={{ flex: 1, minWidth: 220 }}
              styles={{ label: { width: "100%" } }}
              size="md"
              onKeyDown={(e) => {
                if (e.key === "Enter") void fetchTerminal();
              }}
            />
            <div className={classes.searchActions}>
              <Button
                leftSection={<IconSearch size={18} />}
                onClick={() => void fetchTerminal()}
                loading={loading}
                size="md"
                className={classes.searchActionBtn}
              >
                검색
              </Button>
              <SegmentedControl
                value={queueDescending ? "desc" : "asc"}
                onChange={(value) => {
                  lastCompletedSerialRef.current = null;
                  setSequenceGap(null);
                  setGapModal(null);
                  setQueueDescending(value === "desc");
                }}
                data={[
                  { label: "오름차순", value: "asc" },
                  { label: "내림차순", value: "desc" },
                ]}
                color="blue"
                size="md"
                classNames={{
                  root: classes.sortControl,
                  label: classes.sortControlLabel,
                  control: classes.sortControlItem,
                }}
              />
            </div>
          </Flex>
        </Paper>

        {displayQueue.length > 0 && (
          <Paper p="sm" withBorder radius="md" className={classes.queueCompact}>
            <Flex
              justify="space-between"
              align="center"
              gap="sm"
              mb={6}
              wrap="wrap"
            >
              <Text size="sm" fw={600}>
                주입 목록
              </Text>
              <Text size="xs" c="dimmed">
                {navQueueCount}건 · 대기 {pendingWorkCount}건
              </Text>
            </Flex>
            <Flex align="center" gap={4} className={classes.queueScrollRow}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="이전 목록"
                onClick={() => scrollQueueBy(-1)}
                disabled={displayQueue.length === 0}
              >
                <IconChevronLeft size={16} />
              </ActionIcon>
              <div ref={queueScrollRef} className={classes.queueScroll}>
                {displayQueue.map((row) => {
                  const isCurrent = terminal?.spSerialNo === row.spSerialNo;
                  const isLookupAnchor = row.spSerialNo === queueFrom;
                  const isNextWork =
                    row.spSerialNo === nextWorkSn && !isCurrent;
                  const rowDone = isInjectWorkComplete(row);
                  return (
                    <span
                      key={row.spSerialNo}
                      data-queue-sn={row.spSerialNo}
                      className={classes.queueBadge}
                    >
                      <Badge
                        size="sm"
                        variant={isCurrent || rowDone ? "filled" : "light"}
                        color={
                          isCurrent
                            ? "red"
                            : rowDone
                              ? "green"
                              : isNextWork
                                ? "teal"
                                : isLookupAnchor
                                  ? "orange"
                                  : "blue"
                        }
                        style={{ cursor: "pointer" }}
                        onClick={() => applyTerminalFromRow(row)}
                        title={`S/N:${isInjected(row.spSerialInjectionYn) ? "Y" : "N"} DIK:${hasDikKey(row) ? (isInjected(row.refInjectionYn) ? "Y" : "N") : "-"} MAC:${isInjected(row.macInjectionYn) ? "Y" : "N"}`}
                      >
                        {row.spSerialNo}
                        {isCurrent ? " · 작업" : ""}
                        {!isCurrent && isNextWork ? " · 다음" : ""}
                        {!isCurrent && !isNextWork && rowDone ? " · 완료" : ""}
                      </Badge>
                    </span>
                  );
                })}
              </div>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="다음 목록"
                onClick={() => scrollQueueBy(1)}
                disabled={displayQueue.length === 0}
              >
                <IconChevronRight size={16} />
              </ActionIcon>
            </Flex>
          </Paper>
        )}

        {terminal && (
          <div className={classes.metaBar}>
            <span>
              <em>모델</em>
              {[terminal.internalModelName, terminal.externalModelName]
                .filter(Boolean)
                .join(" / ") || "—"}
            </span>
            <span>
              <em>S/N</em>
              {terminal.serialNo?.trim() || "(미등록)"}
            </span>
          </div>
        )}

        <div className={`${classes.hero} ${!terminal ? classes.heroIdle : ""}`}>
          <div className={classes.heroNavRow}>
            <ActionIcon
              className={classes.heroArrow}
              variant="light"
              color="teal"
              size={48}
              radius="xl"
              onClick={goToPrevSerial}
              disabled={!canGoPrev}
              aria-label="이전 S/N"
            >
              <IconChevronLeft size={32} stroke={2} />
            </ActionIcon>

            <div className={classes.heroMain}>
              <div className={classes.heroDualGrid}>
                <div
                  className={[
                    classes.heroPanel,
                    classes.heroPanelSerial,
                    !terminal
                      ? classes.heroPanelIdle
                      : sessionPhase === "serial" &&
                        !isInjected(terminal.spSerialInjectionYn)
                        ? classes.heroPanelActive
                        : isInjected(terminal.spSerialInjectionYn)
                          ? classes.heroPanelDone
                          : classes.heroPanelPending,
                  ].join(" ")}
                >
                  <div className={classes.heroPanelLabel}>1. S/N</div>
                  <div className={classes.heroPanelValue}>
                    {terminal?.spSerialNo || "—"}
                  </div>
                  <div className={classes.heroPanelStatus}>
                    {!terminal
                      ? "검색 필요"
                      : isInjected(terminal.spSerialInjectionYn)
                        ? "완료"
                        : sessionPhase === "serial"
                          ? "주입 대상"
                          : "대기"}
                  </div>
                </div>

                <div
                  className={[
                    classes.heroPanel,
                    classes.heroPanelDik,
                    !terminal
                      ? classes.heroPanelIdle
                      : sessionPhase === "dik" &&
                        !isInjected(terminal.refInjectionYn)
                        ? classes.heroPanelActive
                        : isInjected(terminal.refInjectionYn)
                          ? classes.heroPanelDone
                          : classes.heroPanelPending,
                  ].join(" ")}
                >
                  <div className={classes.heroPanelLabel}>2. DIK</div>
                  <div className={classes.heroPanelValue}>
                    {!terminal
                      ? "—"
                      : isInjected(terminal.refInjectionYn)
                        ? "주입"
                        : "미주입"}
                  </div>
                  <div className={classes.heroPanelStatus}>
                    {!terminal ? "검색 필요" : "\u00a0"}
                  </div>
                </div>

                <div
                  className={[
                    classes.heroPanel,
                    classes.heroPanelMac,
                    !terminal
                      ? classes.heroPanelIdle
                      : sessionPhase === "done" &&
                        !isInjected(terminal.macInjectionYn)
                        ? classes.heroPanelActive
                        : terminal && isInjected(terminal.macInjectionYn)
                          ? classes.heroPanelDone
                          : classes.heroPanelPending,
                  ].join(" ")}
                >
                  <div className={classes.heroPanelLabel}>3. MAC</div>
                  <div className={classes.heroPanelValue}>
                    {formatMacForDisplay(terminal?.macAddress) || "—"}
                  </div>
                  <div className={classes.heroPanelStatus}>
                    {!terminal
                      ? "검색 필요"
                      : sessionPhase === "done" &&
                        !isInjected(terminal.macInjectionYn)
                        ? "주입 대상"
                        : isInjected(terminal.macInjectionYn)
                          ? "완료"
                          : hasDikKey(terminal)
                            ? "DIK 다음"
                            : "S/N 다음"}
                  </div>
                </div>
              </div>

              {terminal && (
                <div className={classes.heroFooter}>
                  {navQueueCount > 0 && currentNavIndex >= 0 && (
                    <Text size="sm" c="dimmed" fw={600}>
                      {currentNavIndex + 1} / {navQueueCount}
                    </Text>
                  )}
                  {sequenceGap && (
                    <Text
                      size="sm"
                      c={sequenceGap.kind === "missing" ? "red" : "teal"}
                      fw={700}
                    >
                      {sequenceGap.message}
                    </Text>
                  )}
                  {heroHint.kind === "work" ? (
                    <Badge
                      size="lg"
                      variant="filled"
                      color="red"
                      className={classes.heroNeedBadge}
                    >
                      {heroHint.need} 미완료
                    </Badge>
                  ) : (
                    <Text
                      size="sm"
                      fw={heroHint.kind === "done" ? 700 : 500}
                      c={heroHint.kind === "done" ? "orange" : "dimmed"}
                    >
                      {heroHint.text}
                    </Text>
                  )}
                </div>
              )}
            </div>

            <ActionIcon
              className={classes.heroArrow}
              variant="light"
              color="teal"
              size={48}
              radius="xl"
              onClick={goToNextSerial}
              disabled={!canGoNext}
              aria-label="다음 S/N"
            >
              <IconChevronRight size={32} stroke={2} />
            </ActionIcon>
          </div>
        </div>

        {a1BannerText ? (
          <div
            className={`${classes.a1Banner} ${sessionPhase === "done" || a1Ready
              ? classes.a1Ready
              : classes.a1Wait
              }`}
          >
            {a1BannerText}
          </div>
        ) : null}
      </div>

      <Flex className={classes.actionBar} gap="sm" wrap="wrap" align="center">
        <Button
          onClick={() => void openPort("sn")}
          disabled={isSnPortOpen}
          color="blue"
          size="md"
          title={isSnPortOpen ? "이미 연결됨" : "단축키: F2"}
        >
          {isSinglePort
            ? isSnPortOpen
              ? "시리얼 연결됨"
              : "시리얼 연결 (F2)"
            : isSnPortOpen
              ? "S/N·DIK 연결됨"
              : "S/N·DIK 연결 (F2)"}
        </Button>
        {!isSinglePort && (
          <Button
            onClick={() => void openPort("mac")}
            disabled={isMacPortOpen}
            color="violet"
            size="md"
            variant={isMacPortOpen ? "light" : "filled"}
            title={
              isMacPortOpen
                ? "이미 연결됨"
                : isSnPortOpen
                  ? "단축키: F2 (다음 선)"
                  : "S/N·DIK 선을 먼저 연결해도 됩니다"
            }
          >
            {isMacPortOpen
              ? "MAC 연결됨"
              : isSnPortOpen
                ? "MAC 연결 (F2)"
                : "MAC 연결"}
          </Button>
        )}
        <Button
          onClick={() => void closePort()}
          disabled={!anyPortOpen}
          variant="default"
          size="md"
          title={
            isSinglePort
              ? "단축키: F3 · 연결 해제"
              : "단축키: F3 · 두 선 모두 해제"
          }
        >
          연결 해제 (F3)
        </Button>
        <Button
          onClick={() => void onSendSerial()}
          color="teal"
          variant="filled"
          size="lg"
          fw={700}
          title="단축키: F4"
        >
          S/N 전송 (F4)
        </Button>
        <Button
          onClick={() => void onSendDik()}
          color="orange"
          variant="filled"
          size="lg"
          fw={700}
          title="단축키: F8"
        >
          DIK 전송 (F8)
        </Button>
        <Button
          onClick={() => void onSendMac()}
          color="violet"
          variant="filled"
          size="lg"
          fw={700}
          title="단축키: F9"
        >
          MAC 전송 (F9)
        </Button>

        <Text size="sm" c={isSnPortOpen ? "green" : "red"}>
          {isSinglePort
            ? isSnPortOpen
              ? snListening
                ? "시리얼 포트 연결"
                : "시리얼 포트 연결(감시 실패)"
              : "시리얼 포트 미연결"
            : isSnPortOpen
              ? snListening
                ? "S/N선 연결"
                : "S/N선 연결(감시 실패)"
              : "S/N선 미연결"}
        </Text>
        {!isSinglePort && (
          <Text size="sm" c={isMacPortOpen ? "green" : "red"}>
            {isMacPortOpen
              ? macListening
                ? "MAC선 연결"
                : "MAC선 연결(감시 실패)"
              : "MAC선 미연결"}
          </Text>
        )}
      </Flex>

      <div className={classes.logSection}>
        <div className={classes.logPanel} ref={logScrollRef}>
          <Flex
            justify="space-between"
            align="center"
            mb={8}
            style={{
              position: "sticky",
              top: 0,
              background: "#f1f5f9",
              zIndex: 1,
            }}
          >
            <Text size="xs" fw={700}>
              통신 로그
            </Text>
            <Flex gap={6} align="center">
              <SegmentedControl
                size="xs"
                value={showSerialDetail ? "detail" : "simple"}
                onChange={(value) => {
                  const next = value === "detail";
                  showSerialDetailRef.current = next;
                  setShowSerialDetail(next);
                }}
                data={[
                  { label: "요약", value: "simple" },
                  { label: "상세", value: "detail" },
                ]}
              />
              <Button
                size="xs"
                variant="default"
                disabled={logGroups.length === 0}
                onClick={clearLogs}
              >
                클리어
              </Button>
            </Flex>
          </Flex>
          {logGroups.length === 0 ? (
            <Text size="xs" c="dimmed">
              {injectMode === "auto"
                ? "자동: 1번→S/N 자동 · DIK는 웹 F8 · MAC선→MAC 자동"
                : "수동: S/N / DIK / MAC 버튼으로 전송"}
            </Text>
          ) : (
            logGroups.map((group) => {
              const style = GROUP_STYLE[group.type];
              const visibleLines = showSerialDetail
                ? group.lines
                : group.lines.filter((line) => line.tone !== "detail");
              const isRxDump = group.title.startsWith("시리얼 수신");
              const isWireTitle = /^[◀▶✗○OX]/.test(group.title);
              if (
                !showSerialDetail &&
                (isRxDump || (visibleLines.length === 0 && !isWireTitle))
              ) {
                return null;
              }
              const isFailure =
                group.title.startsWith("X ") ||
                group.title.includes("주입 실패") ||
                group.title.startsWith("✗");
              const boxBg = isFailure ? "#FF5232" : style.bg;
              const boxBorder = isFailure ? "#b91c1c" : style.border;
              const titleColor = isFailure ? "#ffffff" : style.titleColor;

              return (
                <Box
                  key={group.id}
                  mb={8}
                  style={{
                    borderLeft: `4px solid ${boxBorder}`,
                    background: boxBg,
                    borderRadius: 6,
                    padding: "6px 8px",
                  }}
                >
                  <Text size="xs" fw={700} c={titleColor}>
                    [{group.time}] {group.title}
                  </Text>
                  {visibleLines.map((line, i) => (
                    <div
                      key={i}
                      style={{
                        color: isFailure ? "#ffffff" : lineColor(line.tone),
                        wordBreak: "break-all",
                        fontSize: line.tone === "detail" ? 11 : 12,
                        paddingLeft: line.tone === "detail" ? 10 : 0,
                      }}
                    >
                      <span
                        style={{
                          color: isFailure ? "#fecaca" : "#94a3b8",
                          marginRight: 6,
                        }}
                      >
                        {line.time}
                      </span>
                      {line.text}
                    </div>
                  ))}
                </Box>
              );
            })
          )}
        </div>
      </div>

      <Modal
        opened={!!gapModal}
        onClose={() => setGapModal(null)}
        centered
        size="lg"
        radius="md"
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape
        classNames={{ content: classes.gapModalContent }}
        title={
          <Flex align="center" gap="sm">
            <IconAlertTriangle
              size={28}
              color={gapModal?.kind === "missing" ? "#ca8a04" : "#0d9488"}
            />
            <Text fw={800} size="lg">
              {gapModal?.title ?? "시리얼 번호 확인"}
            </Text>
          </Flex>
        }
      >
        <Text className={classes.gapModalMessage} ta="center" my="xl">
          {gapModal?.message}
        </Text>
        <Flex justify="center">
          <Button
            size="lg"
            color={gapModal?.kind === "missing" ? "yellow" : "teal"}
            w={160}
            onClick={() => setGapModal(null)}
          >
            확인
          </Button>
        </Flex>
      </Modal>
    </div>
  );
};

export default KeyInjectTest;
